import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DEFAULT_ROOMS,
  PUBLIC_ROOMS,
  NPC_CHARACTERS,
  ROTATING_NPCS,
  ROOM_DESCRIPTIONS,
  SYSTEM_COLOR,
  POKEMON_NPC_CHARACTERS,
} from '../mock';
import {
  ViewProfileModal,
  RegisterModal,
  EditProfileModal,
  DeleteUserModal,
  EditNpcModal,
  getProfile,
} from '../components/ProfileModals';
import { TutorialModal, SiteRulesModal, RoomDirectory, MessengerBox, FriendsListBox } from '../components/ChatExtras';
import { WorldMapModal } from '../components/WorldMapModal';
import { LeaderboardModal } from '../components/LeaderboardModal';
import { WhisperShadowConsole } from '../components/WhisperShadowConsole';
import {
  FriendRequestSendModal,
  FriendRequestInboxModal,
  AddFriendSearchModal,
} from '../components/FriendModals';
import { KickDurationModal } from '../components/KickDurationModal';
import { PrivateRoomMonitorModal } from '../components/PrivateRoomMonitorModal';
import { TopTabsBar } from '../components/TopTabsBar';
import { UnreadPmModal } from '../components/UnreadPmModal';
import AdventureSoundtrackPlayer from '../components/AdventureSoundtrackPlayer';
import AdminModPanel from '../components/AdminModPanel';
import { BanUserModal, PuppetNpcModal, AnnounceModal, DeleteUserAdminModal, AddStatPointsModal } from '../components/AdminModModals';
import { AdminEditUserStatsModal, AdminEditNpcAffectionModal } from '../components/AdminStatsEditorModals';
import { PokemonStadiumBattle } from '../components/PokemonStadiumBattle';
import ShadowWhisperMonitorModal from '../components/ShadowWhisperMonitorModal';
import ManageModsModal from '../components/ManageModsModal';
import CreateNpcModal from '../components/CreateNpcModal';
import ManageNpcsModal from '../components/ManageNpcsModal';
import UsersListModal from '../components/UsersListModal';
import AdminEditProfileModal from '../components/AdminEditProfileModal';
import HpPill from '../components/HpPill';
import DailyQuestModal from '../components/DailyQuestModal';
import SceneRecapModal from '../components/SceneRecapModal';
import useChatWebsocket from '../hooks/useChatWebsocket';
import { ActionPillBar } from '../components/ActionPillBar';
import Composer from '../components/Composer';
import { VoiceVideoPanel, subscribeSpeaking } from '../components/VoiceVideoPanel';
import { VOICE_VIDEO_ROOMS, SCREENSHARE_ROOMS, DISCORD_LINKED_ROOMS } from '../mock';
import { runChatCommand } from '../commands';
// Msg #977 — theme palette extracted out of this 2700-line monolith.
import { ROOM_THEMES, roomThemeColor, roomThemePalette } from '../theme';

// Msg #804 — Room-based chat theme colors.  Applied as a CSS background color
// on the main chat frame.  User-created rooms inherit "default"; the
// LoginPage's public-room creator can also pass a color that lands in
// `custom_room_color` in localStorage.
// Msg #977 — theme palette moved to /src/theme.js (see imports above).

// Msg #804 — small notification-sound helpers.  Uses WebAudio so we don't
// need to ship .mp3 assets.  Bling = short two-note ding for PMs; YouGotMail
// = classic 4-tone chime for friend requests.
const _bling = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = 'sine'; o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.linearRampToValueAtTime(1320, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    o.connect(g).connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.3);
    setTimeout(() => ctx.close(), 500);
  } catch { /* ignore */ }
};
const _youGotMail = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 784, 1046.5];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      const t0 = ctx.currentTime + i * 0.14;
      o.type = 'triangle'; o.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0.001, t0);
      g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      o.connect(g).connect(ctx.destination); o.start(t0); o.stop(t0 + 0.36);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch { /* ignore */ }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PALETTE = ['#2F6B2F', '#1F5E3A', '#3A6B1F', '#2B5D45', '#4F7A2A', '#1E5D4A', '#3F7F4A', '#5A7F33', '#2A6A55', '#3E7060'];
const pickColorForUser = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % PALETTE.length;
  return PALETTE[h];
};
const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Inline-format <b><i><u> + bare URLs/emails
const renderInline = (text) => {
  const escaped = String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let s = escaped
    .replace(/&lt;b&gt;([\s\S]*?)&lt;\/b&gt;/gi, '<b>$1</b>')
    .replace(/&lt;i&gt;([\s\S]*?)&lt;\/i&gt;/gi, '<i>$1</i>')
    .replace(/&lt;u&gt;([\s\S]*?)&lt;\/u&gt;/gi, '<u>$1</u>');
  s = s.replace(
    /\b(https?:\/\/[^\s<]+)/gi,
    '<a href="$1" target="_blank" rel="noreferrer" style="color:#1a4a10;text-decoration:underline">$1</a>',
  );
  s = s.replace(
    /([\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g,
    '<a href="mailto:$1" style="color:#1a4a10;text-decoration:underline">$1</a>',
  );
  return s;
};

const ChatPage = () => {
  const navigate = useNavigate();
  const username = sessionStorage.getItem('keep_username') || 'Guest';
  // Msg #973 — default font color is a solid dark #111111 (readable in all
  // white rooms). Users can still pick their own via the compose toolbar.
  const defaultColor = '#111111';
  React.useMemo(() => pickColorForUser(username), [username]); // (side-effect: seed rng)

  // Stable per-USER user-id keyed by username in localStorage. Msg #989 — old
  // sessionStorage-scoped id reset on logout/tab-close, which orphaned all of
  // that character's NPC affection (db.npc_stats is keyed by user_id). Now the
  // id is persisted in localStorage keyed by username, and further hydrated
  // from the /auth/login response on login when the backend has a canonical
  // approved_users.user_id.
  const userId = React.useMemo(() => {
    const key = 'mandala_user_id::' + (username || 'guest').toLowerCase();
    // 1. Prefer login-response canonical id (written to sessionStorage on login)
    let s = sessionStorage.getItem('mandala_login_user_id');
    if (s) {
      localStorage.setItem(key, s);
      // Also mirror to sessionStorage for reads elsewhere in the app.
      sessionStorage.setItem('mandala_user_id', s);
      return s;
    }
    // 2. Fall back to a per-username id in localStorage (persists across
    //    logout, new tabs, and browser restarts).
    s = localStorage.getItem(key);
    if (!s) {
      s = newId();
      localStorage.setItem(key, s);
    }
    sessionStorage.setItem('mandala_user_id', s);
    return s;
  }, [username]);

  const [room, setRoom] = React.useState(sessionStorage.getItem('keep_room') || 'OOC_Room');

  // Ensure every user who has ever entered the chat has at least a minimal
  // profile row on the backend, so /api/profiles/search returns them even
  // when they're logged off. Uses /profiles/touch which uses $setOnInsert
  // so it never overwrites an already-edited profile.
  React.useEffect(() => {
    if (!username || username === 'Guest') return;
    axios.post(`${API}/profiles/touch`, { username }).catch(() => {});
  }, [username]);

  const [messages, setMessages] = React.useState([]);
  const [presenceUsers, setPresenceUsers] = React.useState([]);
  // Msg #797 — Ref mirror so search effect can read the latest presence
  // WITHOUT re-firing on every poll (which used to reset the dropdown scroll).
  const presenceUsersRef = React.useRef([]);
  React.useEffect(() => { presenceUsersRef.current = presenceUsers; }, [presenceUsers]);
  const [npcLocations, setNpcLocations] = React.useState({});
  // Msg #1003 — Metadata table keyed by NPC name → { npc_type, color, gender,
  // is_monster }. Populated from /api/npc_locations so custom NPCs / Pokémon /
  // Pokémorphs get correct (POKEMON) / (POKEMORPH) / (CHAMPION) tags.
  const [npcMetadata, setNpcMetadata] = React.useState({});
  const [input, setInput] = React.useState('');
  const [npcTyping, setNpcTyping] = React.useState('');
  const scrollRef = React.useRef(null);
  const lastTsRef = React.useRef(null);
  const seenIdsRef = React.useRef(new Set());

  // Msg #977 — cross-browser memory fix: cap in-memory messages at 500 so
  // long marathon sessions don't balloon Firefox / mobile Chrome heap.
  const MAX_MESSAGES_IN_MEMORY = 500;
  const trimMessages = React.useCallback((next) => {
    if (next.length <= MAX_MESSAGES_IN_MEMORY) return next;
    return next.slice(next.length - MAX_MESSAGES_IN_MEMORY);
  }, []);

  // Msg #977 — WebSocket live-push (additive; polling stays as fallback).
  const handleWsPush = React.useCallback((frame) => {
    const m = frame && frame.message;
    if (!m || !m.id) return;
    if (frame.room && frame.room !== room) return;
    if (seenIdsRef.current.has(m.id)) return;
    seenIdsRef.current.add(m.id);
    if (m.ts) lastTsRef.current = m.ts;
    setMessages((prev) => trimMessages([...prev, m]));
    if (m.user === npcTyping) setNpcTyping('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, npcTyping, trimMessages]);

  // Msg #980 — WS presence signal: nudge the presence poll immediately when a
  // join/leave/room-change event is broadcast globally. This gets the userlist
  // updating live (within ~250 ms) instead of waiting up to 5 s for the poll.
  const [presenceNudge, setPresenceNudge] = React.useState(0);
  const handleWsPresence = React.useCallback(() => {
    setPresenceNudge((n) => n + 1);
  }, []);
  // Msg #980 — WS whisper push: mirror the poll result path so a whisper shows
  // up instantly without waiting for the 2-second poll.
  const handleWsWhisper = React.useCallback((w) => {
    if (!w || !w.id) return;
    // We rely on the polling merge logic to dedupe by id. Just trigger a
    // fast poll by nudging presence.
    setPresenceNudge((n) => n + 1);
  }, []);
  // Msg #983 — WS handler for nowplaying updates in this room.
  const handleWsNowPlaying = React.useCallback((frame) => {
    if (!frame || (frame.room && frame.room !== room)) return;
    if (frame.cleared || !frame.title) {
      setNowPlaying({ title: '', user: '' });
    } else {
      setNowPlaying({ title: frame.title, user: frame.user || '' });
    }
  }, [room]);
  const wsHandlers = React.useMemo(() => ({
    onMessage: handleWsPush,
    onPresence: handleWsPresence,
    onWhisper: handleWsWhisper,
    onNowPlaying: handleWsNowPlaying,
    userId,
  }), [handleWsPush, handleWsPresence, handleWsWhisper, handleWsNowPlaying, userId]);
  const { connected: wsConnected } = useChatWebsocket(API, room, wsHandlers);

  // chat command state
  // Msg #988 — Prefer saved font color: localStorage (instant paint) → fetch
  // /api/user/font_color for authoritative value on mount → default.
  const [myColor, setMyColor] = React.useState(() => {
    try { return localStorage.getItem('mandala_font_color') || defaultColor; }
    catch { return defaultColor; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('mandala_font_color', myColor); } catch { /* ignore */ }
  }, [myColor]);
  // Fetch backend-persisted color once, then debounce-save on user changes.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/font_color`, { params: { username } });
        if (cancelled) return;
        if (r.data && r.data.font_color && r.data.font_color !== myColor) {
          setMyColor(r.data.font_color);
        }
      } catch (e) { void e; }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);
  const colorSaveTimerRef = React.useRef(null);
  React.useEffect(() => {
    if (!username) return;
    if (colorSaveTimerRef.current) clearTimeout(colorSaveTimerRef.current);
    colorSaveTimerRef.current = setTimeout(() => {
      axios.post(`${API}/user/font_color`, {
        username, font_color: myColor,
      }).catch(() => { /* silent — localStorage still holds it */ });
    }, 750);
    return () => { if (colorSaveTimerRef.current) clearTimeout(colorSaveTimerRef.current); };
  }, [myColor, username]);
  // Session AD (Msg #972) — compose style ('say' = plain, 'action' = wraps in
  // /me action, 'ooc' = ((brackets))). Persisted per browser.
  const [composeMode, setComposeMode] = React.useState(() => {
    try { return localStorage.getItem('mandala_compose_mode') || 'say'; }
    catch { return 'say'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('mandala_compose_mode', composeMode); } catch { /* ignore */ }
  }, [composeMode]);
  const [alias, setAlias] = React.useState('');
  const [showTimestamps, setShowTimestamps] = React.useState(true);
  const [lastTyped, setLastTyped] = React.useState('');
  const [ignoredUsers, setIgnoredUsers] = React.useState(() => new Set());
  const [refreshTick, setRefreshTick] = React.useState(0);
  // Whether to display Narrator "arrives / slips away" system lines.
  const [notifyJoinLeave, setNotifyJoinLeave] = React.useState(true);
  // Polling delay in ms — driven by /refresh [n]. Default 2000 = 2s.
  const [pollDelay, setPollDelay] = React.useState(2000);
  // User-created private rooms remembered locally (id + password) so /join works.
  const [knownPrivateRooms, setKnownPrivateRooms] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('mandala_known_private_rooms') || '[]'); }
    catch (e) { void e; return []; }
  });
  const [roomDescription, setRoomDescription] = React.useState('');

  // Msg #974 — room-scoped typing indicator.
  const [typingPeers, setTypingPeers] = React.useState([]);   // [{name, color}]
  const lastTypingSentRef = React.useRef(0);
  const notifyTyping = React.useCallback(() => {
    const now = Date.now();
    // Throttle to at most one ping every 2.5s.
    if (now - lastTypingSentRef.current < 2500) return;
    lastTypingSentRef.current = now;
    try {
      axios.post(`${API}/chat/typing`, {
        user_id: userId, name: username, room, color: myColor,
      }).catch(() => {});
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, username, room, myColor]);
  const clearTypingNow = React.useCallback(() => {
    lastTypingSentRef.current = 0;
    try {
      axios.post(`${API}/chat/typing/clear`, {
        user_id: userId, name: username, room, color: myColor,
      }).catch(() => {});
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, username, room, myColor]);

  React.useEffect(() => {
    if (!userId || !room) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/chat/typing_status?room=${encodeURIComponent(room)}&exclude_user=${encodeURIComponent(userId)}`
        );
        if (!cancelled) setTypingPeers(r.data.typing || []);
      } catch { /* ignore */ }
    };
    tick();
    // Msg #1001 — Typing events also arrive via WebSocket (`type: typing`).
    // Back the poll off from 3 s → 10 s when WS is connected. Kept as a
    // safety-net for browsers that dropped the socket.
    const id = setInterval(tick, wsConnected ? 10000 : 3000);
    return () => {
      cancelled = true; clearInterval(id);
      // Msg #974 — leaving this room? Immediately drop our typing entry so we
      // don't linger as "typing" on the room we just left.
      try {
        axios.post(`${API}/chat/typing/clear`, {
          user_id: userId, name: username, room, color: myColor,
        }).catch(() => {});
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, room, wsConnected]);

  // modals
  const [viewChar, setViewChar] = React.useState(null);
  const [showRegister, setShowRegister] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [showDelete, setShowDelete] = React.useState(false);
  const [showTutorial, setShowTutorial] = React.useState(false);
  const [showWorldMap, setShowWorldMap] = React.useState(false);
  const [showLeaderboard, setShowLeaderboard] = React.useState(false);
  // Msg #845 — friend request flows
  const [friendReqTarget, setFriendReqTarget] = React.useState(null); // outbound request modal
  const [showFriendInbox, setShowFriendInbox] = React.useState(false); // incoming inbox
  const [showAddFriend, setShowAddFriend] = React.useState(false);     // search-and-add modal
  const [pendingInboxCount, setPendingInboxCount] = React.useState(0);
  // Msg #981 — voice/video panel
  const [voiceOpen, setVoiceOpen] = React.useState(false);
  const [voiceReady, setVoiceReady] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/rtc/config`).then((r) => {
      if (!cancelled) setVoiceReady(!!(r.data && r.data.ready));
    }).catch(() => { if (!cancelled) setVoiceReady(false); });
    return () => { cancelled = true; };
  }, []);

  // Msg #982 — Auto-mount voice panel on entry to Hangout / Movie_Night.
  // Voice room preset: camera on. Movie_Night preset: camera off (screenshare).
  const voiceRoomMode = React.useMemo(() => {
    if (VOICE_VIDEO_ROOMS.has(room)) return 'voice_video';
    if (SCREENSHARE_ROOMS.has(room)) return 'screenshare';
    return null;
  }, [room]);
  React.useEffect(() => {
    if (voiceRoomMode && voiceReady) {
      setVoiceOpen(true);
    } else if (voiceRoomMode === null) {
      // Auto-close when leaving a voice-preset room to avoid dangling connection.
      setVoiceOpen(false);
    }
  }, [voiceRoomMode, voiceReady]);

  // Msg #983 — Per-room "Now Watching" marquee (persistent header). Fetched
  // on room-change and refreshed live via WS.
  const [nowPlaying, setNowPlaying] = React.useState({ title: '', user: '' });
  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/room/nowplaying`, { params: { room } })
      .then((r) => {
        if (cancelled) return;
        setNowPlaying({
          title: r.data.title || '',
          user: r.data.user || '',
        });
      })
      .catch(() => { if (!cancelled) setNowPlaying({ title: '', user: '' }); });
    return () => { cancelled = true; };
  }, [room]);

  // Msg #982 — Speaking-in-voice identity set (fed by <VoiceVideoPanel>).
  const [speakingSet, setSpeakingSet] = React.useState(new Set());
  React.useEffect(() => subscribeSpeaking(setSpeakingSet), []);

  // Msg #892 — admin cloak (invisible mode)
  const [cloaked, setCloaked] = React.useState(false);

  // Msg #979 — active /arouse biases against me (list of NPC names)
  const [arousedNpcs, setArousedNpcs] = React.useState([]);
  React.useEffect(() => {
    if (!username) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/npc/arousals`, { params: { user: username } });
        if (!cancelled) setArousedNpcs(r.data?.aroused || []);
      } catch (e) { void e; }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [username]);

  React.useEffect(() => {
    if (!username) return;
    axios.get(`${API}/admin/cloak`, { params: { username } })
      .then((r) => setCloaked(!!r.data?.cloaked))
      .catch(() => {});
  }, [username]);
  const toggleCloak = React.useCallback(async (next) => {
    try {
      const r = await axios.post(`${API}/admin/cloak`, { requester: username, cloaked: !!next });
      setCloaked(!!r.data?.cloaked);
    } catch { /* ignore */ }
  }, [username]);
  const [showRules, setShowRules] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isMod, setIsMod] = React.useState(false); // Msg #774 — mod-only (kick/ban) role
  // Msg #995 — WhisperShadowConsole visibility override — driven manually by
  // the "RECAP" pill in the ADMIN group. Defaults null (auto = isAdmin && cloaked).
  const [whisperShadowOverride, setWhisperShadowOverride] = React.useState(null);
  // Msg #804 — Admin/Mod panel modals + private-rooms-allowed toggle.
  const [showBanModal, setShowBanModal] = React.useState(false);
  const [showPuppetModal, setShowPuppetModal] = React.useState(false);
  const [showAnnounceModal, setShowAnnounceModal] = React.useState(false);
  // Msg #986 — Admin: delete user + shadow whisper modals
  const [showDeleteUserModal, setShowDeleteUserModal] = React.useState(false);
  const [showShadowWhisperModal, setShowShadowWhisperModal] = React.useState(false);
  // Msg #987 — Admin: manage mods modal
  const [showManageModsModal, setShowManageModsModal] = React.useState(false);
  // Msg #988 — Admin: grant stat points modal (legacy quick-grant form)
  const [showAddStatPointsModal, setShowAddStatPointsModal] = React.useState(false);
  // Msg #994 — Admin: view+edit user stats popup & view+edit NPC affection popup
  const [showEditUserStatsModal, setShowEditUserStatsModal] = React.useState(false);
  const [showEditNpcAffectionModal, setShowEditNpcAffectionModal] = React.useState(false);
  // Msg #990 — Admin: custom NPC create + manage modals
  const [showCreateNpcModal, setShowCreateNpcModal] = React.useState(false);
  const [showManageNpcsModal, setShowManageNpcsModal] = React.useState(false);
  const [editingNpc, setEditingNpc] = React.useState(null);
  // Msg #991 — Admin: users-list modal + admin-edit-target
  const [showUsersListModal, setShowUsersListModal] = React.useState(false);
  const [adminEditTarget, setAdminEditTarget] = React.useState(null);
  // Msg #992 — Admin box collapsed/expanded (persisted per browser).
  const [adminBoxOpen, setAdminBoxOpen] = React.useState(() => {
    try { return localStorage.getItem('mandala_admin_box_open') !== 'false'; }
    catch { return true; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('mandala_admin_box_open', String(adminBoxOpen)); } catch { /* ignore */ }
  }, [adminBoxOpen]);
  // Msg #975 — daily quest board
  const [showQuestBoard, setShowQuestBoard] = React.useState(false);
  // Msg #977 — Scene Recap modal
  const [showSceneRecap, setShowSceneRecap] = React.useState(false);
  const [privateRoomsAllowed, setPrivateRoomsAllowed] = React.useState(true);
  // Msg #774 — kick-duration modal state.  target = null means closed.
  const [kickModal, setKickModal] = React.useState(null);
  // { target: string, action: 'kick' | 'ban' }
  const [showPrivateRoomMonitor, setShowPrivateRoomMonitor] = React.useState(false);
  // Msg #774 — Admin NPC Takeover.  When set, all outgoing chat messages are
  // posted through /admin/say_as as this NPC instead of the admin's own name.
  const [puppetNpc, setPuppetNpc] = React.useState(null);
  // Msg #786 — Login-time unread-PM popup state.
  const [unreadInfo, setUnreadInfo] = React.useState({ total: 0, by_sender: [] });
  const [unreadModalOpen, setUnreadModalOpen] = React.useState(false);
  const unreadModalShownRef = React.useRef(false);
  // Msg #791 — typing indicator
  const [typingBySender, setTypingBySender] = React.useState({});
  const lastTypingHeartbeatRef = React.useRef(0);

  // Msg #786 — Fetch unread PM count once on login and show popup if > 0.
  React.useEffect(() => {
    if (!userId || unreadModalShownRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/whisper/unread_count`, {
          params: { user_id: userId },
        });
        if (cancelled) return;
        const total = Number(r.data.total || 0);
        setUnreadInfo({ total, by_sender: r.data.by_sender || [] });
        if (total > 0) {
          setUnreadModalOpen(true);
          unreadModalShownRef.current = true;
        }
      } catch (e) { void e; }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Msg #791 — poll typing_status every ~2s.  Maintain a { name_lc: true } map.
  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/whisper/typing_status`, { params: { user_id: userId } });
        if (cancelled) return;
        const next = {};
        (r.data.typing || []).forEach((t) => {
          if (t.from_name) next[String(t.from_name).toLowerCase()] = true;
        });
        setTypingBySender(next);
      } catch (e) { void e; }
    };
    poll();
    // Msg #1001 — Whisper typing events arrive via WS too — back the poll
    // off from 2.2 s → 8 s when WS is connected.
    const id = setInterval(poll, wsConnected ? 8000 : 2200);
    return () => { cancelled = true; clearInterval(id); };
  }, [userId, wsConnected]);

  // Msg #791 — throttled heartbeat while user is typing in Messenger compose.
  // Uses a localStorage lookup for the name to avoid TDZ on `displayName`
  // (which is declared further down in the component).
  const sendTypingHeartbeat = React.useCallback((toName, toUserId) => {
    if (!userId || !toName) return;
    const now = Date.now();
    if (now - (lastTypingHeartbeatRef.current || 0) < 1800) return;  // throttle to ~1/2s
    lastTypingHeartbeatRef.current = now;
    const fromName = sessionStorage.getItem('keep_username') || 'Guest';
    axios.post(`${API}/whisper/typing`, {
      from_user_id: userId,
      from_name: fromName,
      to_user_id: toUserId || undefined,
      to_name: toName,
    }).catch(() => {});
  }, [userId]);
  // user opens that conversation in the Messenger.
  const markConversationRead = React.useCallback(async (fromName, fromUserId) => {
    if (!userId || (!fromName && !fromUserId)) return;
    // Msg #802 — Immediately clear the local unread badge (previously we
    // relied solely on the server round-trip which meant the count never
    // reset until the next poll).
    if (fromName) {
      const key = String(fromName).toLowerCase();
      setPmReadAt((prev) => ({ ...prev, [key]: new Date().toISOString() }));
    }
    try {
      await axios.post(`${API}/whisper/mark_read`, {
        user_id: userId,
        from_name: fromName || undefined,
        from_user_id: fromUserId || undefined,
      });
      // Local optimistic update so ✓✓ appears without a poll roundtrip.
      const now = new Date().toISOString();
      setWhispers((prev) => prev.map((w) => {
        if (w.to_user_id !== userId) return w;
        if (fromUserId && w.from_user_id !== fromUserId) return w;
        if (fromName && !fromUserId && w.from_name !== fromName) return w;
        return w.read_at ? w : { ...w, read_at: now };
      }));
      // Refresh unread counter
      const r = await axios.get(`${API}/whisper/unread_count`, {
        params: { user_id: userId },
      });
      setUnreadInfo({ total: Number(r.data.total || 0), by_sender: r.data.by_sender || [] });
    } catch (e) { void e; }
  }, [userId]);

  // PM / Messenger box
  const [whispers, setWhispers] = React.useState([]);
  const [pmOpen, setPmOpen] = React.useState(false);
  const [pmMinimized, setPmMinimized] = React.useState(false);
  const [pmReadAt, setPmReadAt] = React.useState({}); // { lowerKey: tsString }
  const [closedConvs, setClosedConvs] = React.useState(new Set());
  const [requestedPmKey, setRequestedPmKey] = React.useState(null);
  // Msg #993 — Standalone Friends List Box (decoupled from Messenger).
  const [friendsBoxOpen, setFriendsBoxOpen] = React.useState(false);
  const [friendsBoxMin, setFriendsBoxMin] = React.useState(false);

  // "Continue this scene" — open the messenger box and select the NPC's conversation
  const continueScene = React.useCallback((character) => {
    if (!character || !character.name) return;
    const key = String(character.name).toLowerCase();
    // Make sure the conversation isn't hidden by a prior close
    setClosedConvs((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev); next.delete(key); return next;
    });
    setPmOpen(true);
    setPmMinimized(false);
    setRequestedPmKey(key);
    setViewChar(null);
  }, []);

  // Local + server-side block list
  const [blockedUsers, setBlockedUsers] = React.useState(new Set());
  React.useEffect(() => {
    const loadBlocks = async () => {
      try {
        const r = await axios.get(`${API}/blocks`, { params: { user_id: userId } });
        setBlockedUsers(new Set((r.data.blocked || []).map((s) => String(s).toLowerCase())));
      } catch (e) { void e; }
    };
    loadBlocks();
  }, [userId]);
  const blockUser = React.useCallback(async (u) => {
    const name = u && u.name;
    if (!name) return;
    // Toggle if requested
    if (u._toggle === 'unblock') {
      const key = String(name).toLowerCase();
      setBlockedUsers((prev) => { const n = new Set(prev); n.delete(key); return n; });
      try { await axios.post(`${API}/blocks/remove`, { user_id: userId, blocked_name: name }); } catch (e) { void e; }
      pushLocalSystem(`You are no longer ignoring ${name}.`);
      return;
    }
    const key = String(name).toLowerCase();
    setBlockedUsers((prev) => new Set([...prev, key]));
    setClosedConvs((prev) => new Set([...prev, key]));
    try {
      await axios.post(`${API}/blocks/add`, { user_id: userId, blocked_name: name });
      pushLocalSystem(`You are now ignoring ${name}.`);
    } catch (e) { void e; }
  }, [userId]);
  const unblockUser = React.useCallback(async (u) => {
    const name = u && u.name; if (!name) return;
    const key = String(name).toLowerCase();
    setBlockedUsers((prev) => { const n = new Set(prev); n.delete(key); return n; });
    try {
      await axios.post(`${API}/blocks/remove`, { user_id: userId, blocked_name: name });
      pushLocalSystem(`You are no longer ignoring ${name}.`);
    } catch (e) { void e; }
  }, [userId]);
  const closeConversation = React.useCallback((key) => {
    setClosedConvs((prev) => new Set([...prev, key]));
  }, []);
  // Msg #786 — Restore a previously closed conversation (auto-called when the
  // user clicks a tab / friend row for a person whose thread was hidden).
  const reopenConversation = React.useCallback((key) => {
    if (!key) return;
    setClosedConvs((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // Friends list (server-side, per user)
  const [friends, setFriends] = React.useState([]);
  const refreshFriends = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/friends`, { params: { user_id: userId, username } });
      setFriends(r.data.friends || []);
    } catch (e) { void e; }
  }, [userId, username]);
  React.useEffect(() => {
    refreshFriends();
    // Re-poll every 15s so externally-added friends + cross-tab adds show up.
    const id = setInterval(refreshFriends, 15000);
    return () => clearInterval(id);
  }, [refreshFriends]);
  // Msg #845 — poll for incoming friend requests so the header badge stays fresh.
  const [friendReqToast, setFriendReqToast] = React.useState(null); // Msg #804
  const prevPendingInboxRef = React.useRef(0);
  React.useEffect(() => {
    if (!username) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/friends/requests`, { params: { username } });
        if (!cancelled) {
          const count = r.data?.count || 0;
          setPendingInboxCount(count);
          // Msg #804 — Sound + popup when a NEW friend request lands.
          if (count > prevPendingInboxRef.current) {
            const requests = r.data?.requests || [];
            const latest = requests[0];
            _youGotMail();
            setFriendReqToast({
              from: latest?.from || 'A patron',
              at: Date.now(),
            });
            setTimeout(() => setFriendReqToast(null), 8000);
          }
          prevPendingInboxRef.current = count;
        }
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [username]);
  const addFriend = React.useCallback(async (name) => {
    if (!name) return;
    try {
      await axios.post(`${API}/friends/add`, { user_id: userId, friend_name: name });
      setFriends((prev) => prev.includes(name) ? prev : [...prev, name]);
      pushLocalSystem(`${name} added to your friends.`);
    } catch (e) { void e; }
  }, [userId]);
  const removeFriend = React.useCallback(async (name) => {
    if (!name) return;
    try {
      await axios.post(`${API}/friends/remove`, { user_id: userId, friend_name: name });
      setFriends((prev) => prev.filter((n) => n !== name));
    } catch (e) { void e; }
  }, [userId]);

  // Online users set (lowercase names) — used by MessengerBox to show online dots for friends.
  // Real users: only when `available !== false`. NPCs: online whenever they have a location.
  const onlineUsers = React.useMemo(() => {
    const s = new Set();
    presenceUsers.forEach((u) => {
      if (u.available !== false) s.add(String(u.name).toLowerCase());
    });
    Object.entries(npcLocations || {}).forEach(([name, loc]) => {
      if (loc) s.add(String(name).toLowerCase());
    });
    return s;
  }, [presenceUsers, npcLocations]);

  // Open PM with a specific character — wired from "PM" button in View Profile modal.
  const openPmWith = React.useCallback((character) => {
    if (!character || !character.name) return;
    const key = String(character.name).toLowerCase();
    setClosedConvs((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev); next.delete(key); return next;
    });
    setPmOpen(true);
    setPmMinimized(false);
    setRequestedPmKey(key);
    setViewChar(null);
  }, []);

  // Profile search (below SAY row) — calls /api/profiles/search
  // Also searches NPCs/Monsters (from mock.js) and currently-online users (from presenceUsers).
  const [searchQ, setSearchQ] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searching, setSearching] = React.useState(false);

  // Flat directory of every known NPC + Monster (Lyriana, Dungeon_Master, rotators, monsters).
  // Includes picture / description / color / gender — so each is fully viewable.
  const ALL_NPC_DIRECTORY = React.useMemo(() => {
    const arr = [];
    Object.values(NPC_CHARACTERS || {}).forEach((list) => {
      (list || []).forEach((n) => arr.push({ ...n, isNPC: true }));
    });
    (ROTATING_NPCS || []).forEach((n) => arr.push({ ...n, isNPC: true }));
    // Msg #987 — POKEMON / POKEMORPH NPCs (roam Pokemon rooms only).
    (POKEMON_NPC_CHARACTERS || []).forEach((n) => arr.push({ ...n, isNPC: true }));
    return arr;
  }, []);

  React.useEffect(() => {
    const q = searchQ.trim();
    if (!q) { setSearchResults([]); setSearchOpen(false); return; }
    const ql = q.toLowerCase();
    let cancelled = false;
    setSearching(true);
    const id = setTimeout(async () => {
      // 1) Local NPC + Monster matches (always available, even offline)
      const npcMatches = ALL_NPC_DIRECTORY
        .filter((n) => String(n.name || '').toLowerCase().includes(ql)
                    || String(n.firstname || '').toLowerCase().includes(ql)
                    || String(n.lastname || '').toLowerCase().includes(ql))
        .slice(0, 20)
        .map((n) => ({
          source: 'npc',
          username: n.name,
          firstname: n.firstname || '',
          lastname: n.lastname || '',
          gender: n.gender || '',
          picture: n.picture || '',
          color: n.color || '',
          description: n.description || '',
          _raw: n,
        }));

      // 2) Currently-online real users (even if they never saved a profile)
      // Msg #797 — read latest presenceUsers via ref so the effect does NOT
      // depend on presenceUsers and no longer re-fires (and scroll-resets the
      // dropdown) every polling tick.
      const currentPresence = presenceUsersRef.current || [];
      const onlineMatches = currentPresence
        .filter((u) => String(u.name || '').toLowerCase().includes(ql))
        .filter((u) => !npcMatches.find((n) => String(n.username).toLowerCase() === String(u.name).toLowerCase()))
        .slice(0, 20)
        .map((u) => ({
          source: 'online',
          username: u.name,
          firstname: '',
          lastname: '',
          gender: u.gender || '',
          color: u.color || '',
          isSelf: u.user_id === userId,
        }));

      // 3) Registered profiles from backend (covers logged-off users too)
      let serverMatches = [];
      try {
        const r = await axios.get(`${API}/profiles/search`, { params: { q, limit: 20 } });
        serverMatches = (r.data.results || []).map((p) => ({ source: 'profile', ...p }));
      } catch (e) { void e; }

      // De-duplicate: prefer NPC > server profile > online presence.
      const seen = new Set();
      const merged = [];
      const pushUnique = (rows) => {
        rows.forEach((row) => {
          const k = String(row.username || '').toLowerCase();
          if (!k || seen.has(k)) return;
          seen.add(k);
          merged.push(row);
        });
      };
      pushUnique(npcMatches);
      pushUnique(serverMatches);
      pushUnique(onlineMatches);

      if (!cancelled) {
        setSearchResults(merged.slice(0, 30));
        setSearchOpen(true);
        setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, ALL_NPC_DIRECTORY, userId]);

  // Admin Edit NPC modal
  const [editNpcChar, setEditNpcChar] = React.useState(null);
  // Server-provided NPC overrides {npc: {description, picture, color}}
  const [npcOverrides, setNpcOverrides] = React.useState({});
  React.useEffect(() => {
    const fetchOverrides = async () => {
      try {
        const r = await axios.get(`${API}/npc_overrides`);
        setNpcOverrides(r.data.overrides || {});
      } catch (e) { void e; }
    };
    fetchOverrides();
    // Msg #1001 — NPC descriptions/pictures rarely change. Bumped 60 s → 5 min
    // to reduce background chatter. Admin re-fetches happen on the next open
    // of the profile modal via applyOverride().
    const id = setInterval(fetchOverrides, 300000);
    return () => clearInterval(id);
  }, []);

  // Merge any override into a character object before showing/editing it
  const applyOverride = React.useCallback((c) => {
    if (!c || !c.name) return c;
    const ov = npcOverrides[c.name];
    if (!ov) return c;
    return {
      ...c,
      description: ov.description || c.description,
      picture: ov.picture || c.picture,
      // Msg #778 — surface the full gallery + feminine variant for the profile view
      pictures: (ov.pictures && ov.pictures.length) ? ov.pictures : c.pictures,
      feminine_picture: ov.feminine_picture || c.feminine_picture,
      // Msg #797 batch — external website + embedded video
      website: ov.website || c.website,
      video_url: ov.video_url || c.video_url,
      // Msg #798 batch — dedicated GIF slot + music-player HTML embed
      gif_url: ov.gif_url || c.gif_url,
      music_html: ov.music_html || c.music_html,
      color: ov.color || c.color,
    };
  }, [npcOverrides]);

  React.useEffect(() => {
    if (!sessionStorage.getItem('keep_username')) navigate('/login');
  }, [navigate]);

  // Check admin status once
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/check`, { params: { name: username } });
        if (!cancelled) setIsAdmin(!!r.data.is_admin);
      } catch (e) { void e; }
    })();
    return () => { cancelled = true; };
  }, [username]);

  // Msg #774 — Check moderator status (also re-checks when room changes for
  // per-room mod grants). Admins are always considered mods, but we keep the
  // flags separate so the UI can distinguish MOD vs ADMIN badges.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/mods/check`, { params: { name: username, room } });
        if (!cancelled) setIsMod(!!r.data.is_mod);
      } catch (e) { void e; }
    })();
    return () => { cancelled = true; };
  }, [username, room]);

  const displayName = React.useCallback(
    () => (alias ? `${username} (${alias})` : username),
    [alias, username],
  );

  // Build live NPC roster from server-provided locations
  const liveNpcs = React.useMemo(() => {
    const map = {};
    Object.keys(NPC_CHARACTERS).forEach((r) => { map[r] = [...NPC_CHARACTERS[r]]; });

    // Msg #1003 — Build a lookup of every known NPC's static metadata (color,
    // gender, description, npcType) from mock.js rosters, so we can enrich
    // NPCs listed in npcLocations without duplicating fields.
    const staticMeta = {};
    Object.values(NPC_CHARACTERS || {}).forEach((list) => {
      (list || []).forEach((n) => { if (n?.name) staticMeta[n.name] = n; });
    });
    (ROTATING_NPCS || []).forEach((n) => { if (n?.name) staticMeta[n.name] = n; });
    (POKEMON_NPC_CHARACTERS || []).forEach((n) => { if (n?.name) staticMeta[n.name] = n; });

    Object.entries(npcLocations || {}).forEach(([name, where]) => {
      if (!name || !where || where === 'offline') return;
      const isPrivate = String(where).startsWith('private_');
      if (isPrivate && where !== room) return;
      // Avoid duplicating fixed NPCs already seeded from NPC_CHARACTERS.
      const existing = (map[where] || []).find((n) => n?.name === name);
      if (existing) return;
      const s = staticMeta[name] || {};
      const meta = (npcMetadata || {})[name] || {};
      const npcType = (s.npcType || meta.npc_type || 'NPC').toUpperCase();
      if (!map[where]) map[where] = [];
      map[where].push({
        name,
        color: s.color || meta.color || '#5a3a8a',
        gender: s.gender || meta.gender || 'undisclosed',
        firstname: s.firstname || name,
        lastname: s.lastname || `(${npcType})`,
        description: s.description || '',
        isMonster: !!(s.isMonster || meta.is_monster),
        npcType,
        isNPC: true,
        status: `In ${where}`,
      });
    });
    return map;
  }, [npcLocations, npcMetadata, room]);

  // ─── Presence heartbeat ───
  const [available, setAvailable] = React.useState(() => {
    return localStorage.getItem('mandala_available') !== 'false';
  });
  React.useEffect(() => {
    localStorage.setItem('mandala_available', String(available));
  }, [available]);
  const availableRef = React.useRef(available);
  React.useEffect(() => { availableRef.current = available; }, [available]);

  // Msg #987 — "Available for RP" — orthogonal to available/away (blue orb).
  const [rpAvailable, setRpAvailable] = React.useState(() => {
    return localStorage.getItem('mandala_rp_available') === 'true';
  });
  React.useEffect(() => {
    localStorage.setItem('mandala_rp_available', String(rpAvailable));
  }, [rpAvailable]);
  const rpAvailableRef = React.useRef(rpAvailable);
  React.useEffect(() => { rpAvailableRef.current = rpAvailable; }, [rpAvailable]);

  React.useEffect(() => {
    let cancelled = false;
    const heartbeat = async () => {
      try {
        await axios.post(`${API}/presence/heartbeat`, {
          user_id: userId,
          name: displayName(),
          room,
          color: myColor,
          gender: getProfile(username)?.gender || 'undisclosed',
          available: availableRef.current,
          rp: rpAvailableRef.current,
        });
      } catch (e) { void e; }
    };
    heartbeat();
    const id = setInterval(() => { if (!cancelled) heartbeat(); }, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [room, myColor, userId, username, displayName, available, rpAvailable]);

  // Leave on unload
  React.useEffect(() => {
    const leave = () => {
      try {
        navigator.sendBeacon(
          `${API}/presence/leave`,
          new Blob([JSON.stringify({ user_id: userId, name: username, room })],
                   { type: 'application/json' }),
        );
      } catch (e) { void e; }
    };
    window.addEventListener('beforeunload', leave);
    return () => window.removeEventListener('beforeunload', leave);
  }, [userId, username, room]);

  // ─── Poll presence ───
  // Msg #980 — WS presence pushes now nudge this poll immediately (via
  // presenceNudge state increment from useChatWebsocket handlers), so we can
  // widen the fallback interval from 5s to 15s. If the WS drops, the poll
  // still catches drift within 15s.
  React.useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await axios.get(`${API}/presence`, { params: { user_id: userId } });
        if (!cancelled) setPresenceUsers(r.data.users || []);
      } catch (e) { void e; }
    };
    pull();
    const id = setInterval(pull, wsConnected ? 15000 : 5000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, room, presenceNudge, wsConnected]);

  // ─── Poll whispers (private, cross-room) ───
  const lastWhisperTsRef = React.useRef(null);
  const seenWhisperIdsRef = React.useRef(new Set());
  React.useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const params = { user_id: userId, name: displayName() };
        if (lastWhisperTsRef.current) params.since = lastWhisperTsRef.current;
        const r = await axios.get(`${API}/whisper/poll`, { params });
        if (cancelled) return;
        const incoming = r.data.whispers || [];
        if (incoming.length === 0) return;
        // Msg #786 — Split incoming into "genuinely new" (unseen id) and
        // "updates to existing" (already seen — usually a read_at flip).
        const genuinelyNew = incoming.filter((w) => !seenWhisperIdsRef.current.has(w.id));
        const updates = incoming.filter((w) => seenWhisperIdsRef.current.has(w.id));
        genuinelyNew.forEach((w) => seenWhisperIdsRef.current.add(w.id));
        // Advance the polling cursor to the newest ts seen this batch
        // (updated_at is also tracked server-side but ts is fine here since
        // ts always exists and updated_at ≥ ts for updated docs).
        const lastTs = incoming.reduce((mx, w) => (w.ts > mx ? w.ts : mx), lastWhisperTsRef.current || '');
        if (lastTs) lastWhisperTsRef.current = lastTs;
        if (genuinelyNew.length > 0 || updates.length > 0) {
          setWhispers((prev) => {
            let next = prev;
            if (updates.length > 0) {
              const byId = new Map(updates.map((w) => [w.id, w]));
              next = next.map((w) => byId.get(w.id) || w);
            }
            if (genuinelyNew.length > 0) {
              next = [...next, ...genuinelyNew];
            }
            return next;
          });
          // Msg #804 — Bling sound for any inbound whisper addressed TO me.
          const inboundToMe = genuinelyNew.filter(
            (w) => w.to_user_id === userId
              || (w.to_name_lc && w.to_name_lc === (displayName() || '').toLowerCase())
          );
          if (inboundToMe.length > 0) _bling();
          // Msg #774 — echo NEW slash-command whispers into main chat.
          // Msg #989 — Format: "[08:12] [reccult whispers to Morrigan]: text"
          // (sender view) or "[08:12] [reccult whispers to me]: text" (recipient
          // view). Bracketed form makes it visually distinct from normal room chat.
          // Skip whispers I just sent — sendWhisper() already pushed a local
          // echo so we do NOT want a duplicate line from the poll.
          const echoable = genuinelyNew.filter(
            (w) => (w.via || 'cmd') === 'cmd' && w.from_user_id !== userId,
          );
          if (echoable.length > 0) {
            setMessages((prev) => [
              ...prev,
              ...echoable.map((w) => {
                const fromMe = w.from_user_id === userId;
                const label = fromMe
                  ? `[${w.from_name} whispers to ${w.to_name}]`
                  : `[${w.from_name} whispers to me]`;
                return {
                  id: 'wh_' + w.id,
                  user: label,
                  color: w.from_color || '#5a3a8a',
                  text: w.text,
                  action: false, whisper: true,
                  time: new Date(w.ts).toTimeString().slice(0, 5),
                };
              }),
            ]);
          }
          // Surface NPC private-invite hint
          genuinelyNew.forEach((w) => {
            if (w.invite_room && w.from_user_id && w.from_user_id.startsWith('npc:')) {
              pushLocalSystem(`${w.from_name} suggests slipping away to a private room. Type /accept to follow, or ignore to stay.`);
            }
          });
        } else if (r.data.now) {
          lastWhisperTsRef.current = r.data.now;
        }
      } catch (e) { void e; }
    };
    pull();
    // Msg #980 — WS live-push handles most whisper deliveries; presenceNudge
    // triggers an immediate re-poll on new whisper events. Widen fallback
    // interval when the socket is connected.
    const id = setInterval(pull, wsConnected ? 8000 : 2500);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, presenceNudge, wsConnected]);

  // ─── Poll NPC locations ───
  React.useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await axios.get(`${API}/npc_locations`, { params: { user_id: userId } });
        if (!cancelled) {
          setNpcLocations(r.data.locations || {});
          // Msg #1003 — capture NPC metadata table for tag rendering.
          setNpcMetadata(r.data.metadata || {});
        }
      } catch (e) { void e; }
    };
    pull();
    const id = setInterval(pull, 10000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, room]);

  // ─── Room intro (local only) + load recent messages + start polling ───
  React.useEffect(() => {
    let cancelled = false;
    seenIdsRef.current = new Set();
    lastTsRef.current = null;
    setNpcTyping('');

    // Fetch user-set room description (overrides the static intro).
    (async () => {
      try {
        const r = await axios.get(`${API}/rooms/describe`, { params: { room } });
        if (!cancelled) {
          const d = r.data && r.data.description;
          setRoomDescription(d || '');
          if (d) {
            setMessages((mm) => [...mm, {
              id: 'local_room_desc_' + newId(),
              user: 'Narrator', color: '#5a4a1d',
              text: d, action: false, narration: true,
              time: new Date().toTimeString().slice(0, 5),
            }]);
          }
        }
      } catch (e) { void e; }
    })();

    const desc = ROOM_DESCRIPTIONS[room];
    const intro = [];
    if (desc) {
      intro.push({
        id: 'local_narrator_' + newId(),
        user: 'Narrator', color: '#5a4a1d',
        text: desc, action: false, narration: true,
        time: new Date().toTimeString().slice(0, 5),
      });
    }
    setMessages(intro);

    const loadAndPoll = async () => {
      try {
        const r = await axios.get(`${API}/chat/messages`, { params: { room, limit: 50 } });
        if (cancelled) return;
        const fresh = (r.data.messages || []).filter((m) => !seenIdsRef.current.has(m.id));
        fresh.forEach((m) => seenIdsRef.current.add(m.id));
        if (fresh.length > 0) {
          lastTsRef.current = fresh[fresh.length - 1].ts;
          setMessages((prev) => [...prev, ...fresh]);
        } else {
          lastTsRef.current = r.data.now;
        }
      } catch (e) { void e; }
    };
    loadAndPoll();

    // Msg #1001 — Chat messages arrive live via WebSocket (`type: chat_message`).
    // Back the polling safety-net WAY off when WS is connected (15 s instead
    // of 2 s) so we're not doing a full `/chat/messages` fetch every 2 s on
    // every user. The poll is still there as a resync fallback.
    const activeDelay = wsConnected ? 15000 : Math.max(pollDelay, 2000);
    const id = setInterval(async () => {
      if (cancelled) return;
      try {
        const params = { room, limit: 50 };
        if (lastTsRef.current) params.since = lastTsRef.current;
        const r = await axios.get(`${API}/chat/messages`, { params });
        if (cancelled) return;
        const fresh = (r.data.messages || []).filter((m) => !seenIdsRef.current.has(m.id));
        if (fresh.length > 0) {
          fresh.forEach((m) => seenIdsRef.current.add(m.id));
          lastTsRef.current = fresh[fresh.length - 1].ts;
          setMessages((prev) => [...prev, ...fresh]);
          // Clear typing indicator once an NPC reply arrives
          fresh.forEach((m) => { if (m.user === npcTyping) setNpcTyping(''); });
        } else if (r.data.now) {
          lastTsRef.current = r.data.now;
        }
      } catch (e) { void e; }
    }, activeDelay);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, pollDelay, wsConnected]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const pushLocalSystem = (text) => {
    setMessages((m) => [
      ...m,
      { id: 'local_' + newId(), user: 'System', color: '#5a4a1d', text,
        action: false, system: true,
        time: new Date().toTimeString().slice(0, 5) },
    ]);
  };

  const sendToServer = async (text, action) => {
    // Msg #774 — Admin puppet mode.  Route this line as the puppeted NPC.
    if (puppetNpc && isAdmin) {
      try {
        await axios.post(`${API}/admin/say_as`, {
          requester: username,
          character: puppetNpc.name,
          room,
          text,
          is_action: !!action,
        });
        return;
      } catch (err) {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || `Could not puppet ${puppetNpc.name}.`);
        return;
      }
    }
    try {
      await axios.post(`${API}/chat/send`, {
        room, user: displayName(), color: myColor, text, action,
      });
    } catch {
      pushLocalSystem('Failed to send message — please check your connection.');
    }
  };

  // Send a whisper via API. Used by both /whisper command and MessengerBox.
  // via='cmd' → sender sees an echo in main chat log (classic /whisper UX)
  // via='pm'  → whisper is delivered but NEVER shown in main chat; PM box only
  const sendWhisper = React.useCallback(async (toName, text, via = 'cmd') => {
    // Msg #985 — Throw on failure so the caller's UI (MessengerBox handleSend)
    // can keep the draft + display an inline error. The old fire-and-forget
    // pattern silently ate errors and cleared the compose field.
    try {
      await axios.post(`${API}/whisper/send`, {
        from_user_id: userId, from_name: displayName(), from_color: myColor,
        to_name: toName, text, via,
      });
      // Msg #989 — Instant local echo for /whisper /to (via='cmd') so the
      // sender sees "[reccult whispers to <target>] <text>" immediately in the
      // room, without waiting for the 2s poll to fire.
      if (via === 'cmd') {
        setMessages((prev) => [
          ...prev,
          {
            id: 'wh_local_' + Date.now(),
            user: `[${displayName()} whispers to ${toName}]`,
            color: myColor || '#5a3a8a',
            text,
            action: false, whisper: true,
            time: new Date().toTimeString().slice(0, 5),
          },
        ]);
      }
    } catch (err) {
      // Msg #991 — Differentiate 429 rate-limit from other whisper failures.
      const status = err && err.response && err.response.status;
      const detail = err && err.response && err.response.data && err.response.data.detail;
      if (status === 429) {
        pushLocalSystem(`Server is rate-limiting whispers right now — try again in a few seconds.`);
      } else {
        pushLocalSystem(detail || `Cannot send whisper to ${toName}.`);
      }
      throw err;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, displayName, myColor]);

  // Msg #978 — pickResponders returns ALL mentioned NPCs sorted alphabetically
  // so multi-NPC replies play in a stable order (User → Alma → Blanka → …).
  // Falls back to a single random responder if nothing was mentioned.
  const pickResponders = (text) => {
    const npcs = liveNpcs[room] || [];
    if (npcs.length === 0) return [];
    const lower = text.toLowerCase();
    const mentioned = npcs.filter((n) =>
      lower.includes(String(n.name || '').toLowerCase()) ||
      (n.firstname && lower.includes(String(n.firstname).toLowerCase())) ||
      (n.lastname && lower.includes(String(n.lastname).toLowerCase())),
    );
    if (mentioned.length > 0) {
      return [...mentioned]
        .sort((a, b) => String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase()))
        .map((n) => n.name);
    }
    return [npcs[Math.floor(Math.random() * npcs.length)].name];
  };

  // Backwards-compat: some legacy callers still expect a single responder.
  const pickResponder = (text) => {
    const r = pickResponders(text);
    return r.length ? r[0] : null;
  };

  const triggerNpc = async (character, text, action) => {
    setNpcTyping(character);
    try {
      await axios.post(`${API}/npc/trigger`, {
        session_id: userId, room, character,
        speaker: displayName(), user_message: text, is_action: action,
      });
    } catch {
      setNpcTyping('');
    }
  };

  // ─── Command parser (Msg #984 — extracted to /commands/index.js) ───
  const runCommand = (raw) => runChatCommand(raw, {
    API,
    username, userId, room, isAdmin, cloaked,
    liveNpcs, ignoredUsers, showTimestamps, lastTyped,
    knownPrivateRooms, ALL_NPC_DIRECTORY,
    setInput, setMessages, setRoom, setViewChar, setIgnoredUsers,
    setNotifyJoinLeave, setShowTutorial, setShowTimestamps,
    setAlias, setKickModal, setKnownPrivateRooms, setMyColor,
    setPollDelay, setRefreshTick, setRoomDescription, setShowEdit,
    // Msg #987 — expose availability + RP toggles to slash commands.
    setAvailable, setRpAvailable,
    pushLocalSystem, pickResponder, sendToServer, triggerNpc, logout,
    pickColorForUser, sendWhisper, displayName,
  });

  const send = async (overrideText) => {
    // Msg #973 — accept an optional override so callers (action pills, etc.)
    // can dispatch a command without relying on setInput+setTimeout timing.
    const raw = (typeof overrideText === 'string' && overrideText.length > 0) ? overrideText : input;
    const t = (raw || '').trim();
    if (!t) return;
    if (t !== '/!') setLastTyped(t);
    // Clear the input UP-FRONT when we ran with a live override so the user
    // doesn't see the pill's command lingering in the say-bar after dispatch.
    if (overrideText) setInput('');
    // Msg #974 — clear our typing indicator the moment we submit.
    clearTypingNow();

    if (t.startsWith('/') || t.startsWith(':')) {
      if (t.startsWith(':') && !t.startsWith('//')) {
        const body = t.slice(1).trim();
        setInput('');
        await sendToServer(body, true);
        const rs = pickResponders(body);
        for (const r of rs) { await triggerNpc(r, body, true); }
        return;
      }
      const am = t.match(/^\/(me|she|he)\s+([\s\S]+)$/i);
      if (am) {
        const body = am[2];
        setInput('');
        await sendToServer(body, true);
        const rs = pickResponders(body);
        for (const r of rs) { await triggerNpc(r, body, true); }
        return;
      }
      const handled = runCommand(t);
      if (handled) { setInput(''); return; }
    }
    setInput('');
    // Session AD (Msg #972) — apply the user's compose-style choice.
    if (composeMode === 'action') {
      await sendToServer(t, true);
      const rs = pickResponders(t);
      for (const r of rs) { await triggerNpc(r, t, true); }
      return;
    }
    if (composeMode === 'ooc') {
      const wrapped = t.startsWith('((') && t.endsWith('))') ? t : `(( ${t} ))`;
      await sendToServer(wrapped, false);
      // OOC lines are chat noise — do not wake NPCs.
      return;
    }
    await sendToServer(t, false);
    const rs = pickResponders(t);
    for (const r of rs) { await triggerNpc(r, t, false); }
  };

  const logout = async () => {
    try { await axios.post(`${API}/presence/leave`, { user_id: userId, name: username, room }); } catch (e) { void e; }
    sessionStorage.removeItem('keep_username');
    sessionStorage.removeItem('keep_room');
    navigate('/login');
  };

  // Unread whisper count = whispers received (not from me) whose ts > pmReadAt[counterpartyKey]
  // Msg #987 — Also skip whispers with a backend read_at (server-side receipt),
  // otherwise the pill stays > 0 after Messenger poll refreshes overwrite our
  // optimistic local marks.
  const unreadPmCount = React.useMemo(() => {
    let n = 0;
    whispers.forEach((w) => {
      if (w.from_user_id === userId) return;
      if (w.read_at) return;
      // Msg #989 — /whisper (via='cmd') is chatroom-scoped and never rolls
      // into the Messenger PM pill.
      if ((w.via || 'cmd') === 'cmd') return;
      const key = String(w.from_name || '').toLowerCase();
      const readTs = pmReadAt[key];
      if (!readTs || w.ts > readTs) n += 1;
    });
    return n;
  }, [whispers, userId, pmReadAt]);

  const markRead = React.useCallback((key) => {
    setPmReadAt((prev) => ({ ...prev, [key]: new Date().toISOString() }));
  }, []);

  // Presence map for the userlist sidebar — real users + NPCs
  const presence = React.useMemo(() => {
    const map = {};
    DEFAULT_ROOMS.forEach((r) => (map[r] = []));
    PUBLIC_ROOMS.forEach((r) => (map[r] = []));
    // Deduplicate by lowercased username within each room — presence pings can
    // race and leave two rows for the same person otherwise.
    const seen = {};
    presenceUsers.forEach((u) => {
      if (!map[u.room]) map[u.room] = [];
      const key = `${u.room}::${(u.name || '').toLowerCase()}`;
      if (seen[key]) return;
      seen[key] = true;
      map[u.room].push({
        name: u.name, color: u.color, gender: u.gender,
        available: u.available !== false,
        rp: !!u.rp,   // Msg #987 — Available for RP (blue orb + label)
        awaySince: u.away_since || null,
        isSelf: u.user_id === userId,
        // Msg #978 — carry role + rank enrichment through to the userlist so
        // it can render Admin 🌟, Champion 🏆, and Mod ♦ icons.
        is_admin: !!u.is_admin,
        is_moderator: !!u.is_moderator,
        rank: u.rank || 'Novice',
      });
    });
    // Msg #803 — Sort each room's user list alphabetically (self first) so
    // presence-poll races don't shuffle rows on every 2 s tick.
    Object.keys(map).forEach((r) => {
      map[r].sort((a, b) => {
        if (a.isSelf && !b.isSelf) return -1;
        if (!a.isSelf && b.isSelf) return 1;
        return String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase());
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceUsers, refreshTick, userId]);

  const openProfile = (char) => {
    // Always try to hydrate NPC data from ALL_NPC_DIRECTORY.  Callers that only
    // know {name, color} (MessengerBox conversation list, friend list, whisper
    // sender clicks) still get the full character-profile view instead of a
    // generic stub — as long as the name matches a known NPC.
    const nameKey = String(char.name || '').toLowerCase();
    const full = ALL_NPC_DIRECTORY.find(
      (n) => String(n.name).toLowerCase() === nameKey
    );
    if (full) {
      const merged = { ...full, ...char, isNPC: true };
      setViewChar({
        name: merged.name, color: merged.color, status: merged.status, isNPC: true,
        firstname: merged.firstname, lastname: merged.lastname, gender: merged.gender,
        picture: merged.picture, description: merged.description,
        isMonster: !!merged.isMonster,
      });
    } else if (char.isSelf) {
      setViewChar({ name: username, color: myColor, status: `In ${room}` });
    } else {
      setViewChar({ name: char.name, color: char.color, status: `In ${room}` });
    }
  };

  const roomThemeBg = roomThemeColor(room, sessionStorage.getItem('custom_room_color'));
  const roomPalette = roomThemePalette(room);
  const rootThemeVars = {
    '--room-theme-bg': roomThemeBg,
    '--room-theme-bg-light': roomThemeBg,
    // Session AC (Msg #970) — full 6-token palette exposed to every panel.
    '--room-bg': roomPalette.bg,
    '--room-panel': roomPalette.panel,
    '--room-header': roomPalette.header,
    '--room-border': roomPalette.border,
    '--room-text': roomPalette.text,
    '--room-header-text': roomPalette.headerText,
  };
  return (
    <div className="keep-bg min-h-screen" style={rootThemeVars}>
      <div className="keep-header-bar-themed w-full" />

      {/* Msg #803 — Banner ad slot (top of page). Site owner can drop any
          HTML embed (ad-network iframe, static promo image, or leave blank).
          Reads from REACT_APP_TOP_BANNER_HTML or falls back to a placeholder. */}
      {(process.env.REACT_APP_TOP_BANNER_HTML || '').trim() && (
        <div
          data-testid="top-banner-ad"
          style={{
            width: '100%',
            background: '#1a1a1a',
            borderBottom: '1px solid #5a4a1d',
            display: 'flex', justifyContent: 'center',
            padding: 4,
          }}
          dangerouslySetInnerHTML={{ __html: process.env.REACT_APP_TOP_BANNER_HTML }}
        />
      )}
      {!(process.env.REACT_APP_TOP_BANNER_HTML || '').trim() && (
        <div
          data-testid="top-banner-slot"
          className="top-banner-themed"
          style={{
            width: '100%',
            minHeight: 90,
            maxHeight: 120,
            color: '#f5e6c0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Georgia, serif',
            fontSize: 13,
            letterSpacing: 2,
            textTransform: 'uppercase',
            borderBottom: '1px solid #5a4a1d',
          }}
        >
          <span style={{ opacity: 0.85, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>◆  Your advertisement here  ◆</span>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 pt-3 pb-2">
        <div className="keep-frame p-3">
          {/* Header */}
          <table className="w-full" cellPadding="0" cellSpacing="0">
            <tbody>
              <tr>
                <td className="align-middle">
                  <span className="font-bold text-[18px]" style={{ fontFamily: 'Georgia, serif', letterSpacing: '2px' }}>
                    Mandala
                  </span>
                  <span className="ml-3 text-[12px]">
                    Room: <b>{room}</b>
                    {/* Msg #977 — Live WebSocket connection pill */}
                    {wsConnected && (
                      <span
                        data-testid="ws-live-pill"
                        title="Live WebSocket connection — messages arrive instantly, no 3-second poll."
                        style={{
                          marginLeft: 8, padding: '1px 8px',
                          background: '#0f5a1a', color: '#d3ffd0',
                          border: '1px solid #072f0a',
                          fontSize: 10, fontWeight: 'bold',
                          letterSpacing: 1, textTransform: 'uppercase',
                          borderRadius: 10,
                        }}
                      >🟢 Live</span>
                    )}
                    {/* Msg #978 — Scene Recap button moved into WhisperShadowConsole. */}
                    {/* Msg #983 — Now Playing marquee for movie / listening rooms. */}
                    {nowPlaying.title && (
                      <span
                        data-testid="now-playing-marquee"
                        title={nowPlaying.user ? `Marquee set by ${nowPlaying.user}` : 'Now watching'}
                        style={{
                          marginLeft: 8, padding: '1px 10px',
                          background: 'linear-gradient(90deg,#5a1a1a,#a02020)',
                          color: '#f9e6c0',
                          border: '1px solid #3a0e0e',
                          fontSize: 11, fontWeight: 'bold',
                          letterSpacing: 1,
                          borderRadius: 10,
                          maxWidth: 340, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          display: 'inline-block',
                          animation: 'mandala-marquee-flicker 3.2s ease-in-out infinite',
                        }}
                      >🎬 Now Watching: {nowPlaying.title}</span>
                    )}
                    {/* Msg #986 — Watch-Together sync (▶ ⏸ ⏭) — screenshare rooms only. */}
                    {SCREENSHARE_ROOMS.has(room) && nowPlaying.title && (
                      <span
                        data-testid="watchsync-controls"
                        style={{
                          marginLeft: 6, display: 'inline-flex', gap: 4,
                          verticalAlign: 'middle',
                        }}
                      >
                        {['play', 'pause', 'skip'].map((act) => {
                          const glyph = act === 'play' ? '▶' : act === 'pause' ? '⏸' : '⏭';
                          return (
                            <button
                              key={act}
                              data-testid={`watchsync-${act}`}
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/room/watchsync`, {
                                    room, user: username, action: act, playhead: 0,
                                  });
                                  pushLocalSystem(`${glyph} ${act} broadcast to ${room}.`);
                                } catch (e) {
                                  const d = e && e.response && e.response.data && e.response.data.detail;
                                  pushLocalSystem(d || `Watch sync ${act} failed.`);
                                }
                              }}
                              title={`Broadcast ${act} to everyone watching in ${room}`}
                              style={{
                                background: '#1a1a1a', color: '#f9e6c0',
                                border: '1px solid #5a1a1a',
                                padding: '1px 8px', fontSize: 11, fontWeight: 'bold',
                                cursor: 'pointer', borderRadius: 8,
                              }}
                            >{glyph}</button>
                          );
                        })}
                      </span>
                    )}
                    {/* Msg #976 — Uncensored Mode badge for private rooms (Dolphin-Mixtral routing). */}
                    {String(room || '').startsWith('private_') && (
                      <span
                        data-testid="uncensored-mode-badge"
                        title="This is a private room. NPC roleplay here runs through the Dolphin-Mixtral uncensored model."
                        style={{
                          marginLeft: 8, padding: '1px 8px',
                          background: 'linear-gradient(90deg,#0a3a5a,#1a6a9a)',
                          color: '#d3f0ff',
                          border: '1px solid #0a3a5a',
                          fontSize: 10, fontWeight: 'bold',
                          letterSpacing: 1, textTransform: 'uppercase',
                          borderRadius: 10,
                        }}
                      >🐬 Uncensored Mode</span>
                    )}
                    &nbsp;|&nbsp; Logged in as:{' '}
                    <b
                      style={{ color: myColor, cursor: 'pointer', textDecoration: 'underline dotted' }}
                      onClick={() => setViewChar({ name: displayName(), color: myColor, status: `In ${room}` })}
                    >
                      {displayName()}
                    </b>
                    {isAdmin && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          fontWeight: 'bold',
                          color: '#fff',
                          background: '#a02020',
                          padding: '1px 6px',
                          borderRadius: 3,
                          letterSpacing: 1,
                        }}
                      >
                        ADMIN
                      </span>
                    )}
                    {!isAdmin && isMod && (
                      <span
                        data-testid="role-badge-mod"
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          fontWeight: 'bold',
                          color: '#fff',
                          background: '#3a5a8a',
                          padding: '1px 6px',
                          borderRadius: 3,
                          letterSpacing: 1,
                        }}
                      >
                        MOD
                      </span>
                    )}
                    {isAdmin && puppetNpc && (
                      <span
                        data-testid="puppet-indicator"
                        onClick={() => { setPuppetNpc(null); pushLocalSystem('🎭 Puppet released.'); }}
                        title="Click to release puppet"
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          fontWeight: 'bold',
                          color: '#fff',
                          background: '#5a1a8a',
                          padding: '1px 6px',
                          borderRadius: 3,
                          letterSpacing: 1,
                          cursor: 'pointer',
                        }}
                      >
                        🎭 PUPPET: {puppetNpc.name.replace('_', ' ')}
                      </span>
                    )}
                    <button
                      data-testid="availability-toggle"
                      onClick={() => setAvailable((v) => !v)}
                      title={available ? 'Click to set yourself as Away' : 'Click to set yourself Available'}
                      style={{
                        marginLeft: 8,
                        fontSize: 10,
                        fontWeight: 'bold',
                        color: '#fff',
                        background: available ? '#1c6b1c' : '#7a5a1a',
                        border: 'none',
                        padding: '1px 8px',
                        borderRadius: 3,
                        letterSpacing: 1,
                        cursor: 'pointer',
                      }}
                    >
                      {available ? '● AVAILABLE' : '☾ AWAY'}
                    </button>
                    {/* Msg #987 — Available for RP toggle (blue orb). */}
                    <button
                      data-testid="rp-availability-toggle"
                      onClick={() => setRpAvailable((v) => !v)}
                      title={rpAvailable
                        ? 'You are advertising as Available for RP — click to unset'
                        : 'Advertise yourself as Available for RP (blue orb next to your name)'}
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        fontWeight: 'bold',
                        color: '#fff',
                        background: rpAvailable ? '#1e4a8a' : '#556a7a',
                        border: 'none',
                        padding: '1px 8px',
                        borderRadius: 3,
                        letterSpacing: 1,
                        cursor: 'pointer',
                      }}
                    >
                      {rpAvailable ? '🔵 RP READY' : '○ RP'}
                    </button>
                  </span>
                </td>
                <td className="align-middle text-right text-[12px]">
                  <span className="keep-link mx-2" onClick={() => setShowTutorial(true)}>Tutorial</span>
                  <span className="keep-link mx-2" onClick={() => setShowRules(true)}>Site Rules</span>
                  <span className="keep-link mx-2">Forums</span>
                  <button className="keep-btn ml-3" onClick={logout}>Exit</button>
                </td>
              </tr>
            </tbody>
          </table>

          <hr className="keep-divider" />

          <div className="flex items-center gap-3 text-[12px] mb-2" style={{ flexWrap: 'wrap' }}>
            <span>Switch Room:</span>
            <select className="keep-select" value={DEFAULT_ROOMS.includes(room) ? room : ''}
              onChange={(e) => { if (e.target.value) { setRoom(e.target.value); sessionStorage.setItem('keep_room', e.target.value); } }}>
              <option value="">Default Rooms ...</option>
              {DEFAULT_ROOMS.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
            <select className="keep-select" value={PUBLIC_ROOMS.includes(room) ? room : ''}
              onChange={(e) => { if (e.target.value) { setRoom(e.target.value); sessionStorage.setItem('keep_room', e.target.value); } }}>
              <option value="">Public Rooms ...</option>
              {PUBLIC_ROOMS.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
            {/* Msg #778 — Find Profile input moved here (was below chat) */}
            <div
              style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4 }}
              data-testid="profile-search-row"
            >
              <span className="text-[11px]" style={{ color: SYSTEM_COLOR }}>🔍 Find profile:</span>
              <input
                data-testid="profile-search-input"
                className="chat-input"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onFocus={() => { if (searchResults.length > 0) setSearchOpen(true); }}
                onBlur={() => { setTimeout(() => setSearchOpen(false), 150); }}
                placeholder="Search any registered user..."
                style={{ color: '#1c3f1c', width: 220 }}
              />
              {searchQ && (
                <button
                  data-testid="profile-search-clear"
                  className="keep-btn"
                  onClick={() => { setSearchQ(''); setSearchResults([]); setSearchOpen(false); }}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
              {searchOpen && (
                <div
                  data-testid="profile-search-results"
                  style={{
                    position: 'absolute',
                    left: 90, top: '100%',
                    minWidth: 280,
                    background: '#d6e8c5',
                    border: '1px solid #2d6b1e',
                    zIndex: 50,
                    maxHeight: 240,
                    overflowY: 'auto',
                    fontSize: 12,
                    boxShadow: '0 4px 10px rgba(20,40,20,0.25)',
                  }}
                >
                  {searching && (
                    <div style={{ padding: '6px 10px', fontStyle: 'italic', color: '#3a5a3a' }}>Searching…</div>
                  )}
                  {!searching && searchResults.length === 0 && (
                    <div style={{ padding: '6px 10px', fontStyle: 'italic', color: '#3a5a3a' }}>
                      No profiles match &laquo;{searchQ}&raquo;.
                    </div>
                  )}
                  {!searching && searchResults.map((p) => (
                    <div
                      key={`${p.source}-${p.username}`}
                      data-testid={`profile-search-result-${p.username}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSearchOpen(false);
                        const isNpc = p.source === 'npc';
                        setViewChar({
                          name: p.username,
                          color: p.color || pickColorForUser(p.username),
                          status: isNpc ? (p._raw && p._raw.status) || 'NPC' : (p.source === 'online' ? `In ${room}` : 'searched'),
                          gender: p.gender,
                          firstname: p.firstname,
                          lastname: p.lastname,
                          picture: p.picture,
                          description: p.description,
                          isNPC: isNpc,
                        });
                      }}
                      style={{
                        padding: '5px 10px',
                        borderBottom: '1px dotted #5b8c47',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <span
                        data-testid={`profile-search-result-kind-${p.username}`}
                        title={p.source}
                        style={{
                          fontSize: 9,
                          background: p.source === 'npc' ? '#5a2a8a' : (p.source === 'online' ? '#1f9d3a' : '#3a5a3a'),
                          color: '#ecfcd9',
                          padding: '1px 5px',
                          borderRadius: 2,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontWeight: 'bold',
                        }}
                      >
                        {p.source === 'npc' ? 'NPC' : (p.source === 'online' ? 'Online' : 'User')}
                      </span>
                      <span style={{ fontWeight: 'bold', color: p.color || pickColorForUser(p.username) }}>{p.username}</span>
                      {p.firstname || p.lastname ? (
                        <span style={{ color: '#3a5a3a' }}>
                          {[p.firstname, p.lastname].filter(Boolean).join(' ')}
                        </span>
                      ) : null}
                      {p.gender && (
                        <span style={{ color: '#5a7a3a', fontStyle: 'italic', textTransform: 'capitalize' }}>
                          ({p.gender})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span className="ml-auto text-[11px] text-gray-600">
              Tip: <b>/me</b> action, <b>/roll 2d6</b>, <b>/whisper</b>, <b>/clear</b> — see{' '}
              <span className="keep-link" onClick={() => setShowTutorial(true)}>Tutorial</span>.
            </span>
          </div>

          <table className="w-full chat-layout-table" cellPadding="0" cellSpacing="0">
            <tbody>
              <tr>
                <td className="align-top" style={{ width: '72%' }}>
                  <div
                    className="chat-message-area"
                    data-dark={(room === 'MainHall' || room === 'OOC_Room') ? '1' : '0'}
                    ref={scrollRef}
                  >
                    {(() => {
                      const seen = new Set();
                      const NARRATOR_JOIN_LEAVE = /(arrives|slips quietly away|slips away|returns,)/i;
                      return messages
                        .filter((m) => !ignoredUsers.has(m.user))
                        .filter((m) => !blockedUsers.has(String(m.user || '').split('(')[0].trim().toLowerCase()))
                        .filter((m) => {
                          // /notify off — hide Narrator-driven NPC enter/exit lines (current room only).
                          if (!notifyJoinLeave && m.user === 'Narrator' && m.system && NARRATOR_JOIN_LEAVE.test(m.text || '')) return false;
                          if (seen.has(m.id)) return false;
                          seen.add(m.id);
                          return true;
                        });
                    })().map((m) => {
                      // ▢ icon: show for ANY NPC chat line (not just NPCs currently
                      // 'live' in the room — Lyriana, Dungeon_Master and other static
                      // NPCs should still be saveable when their lines surface in
                      // other rooms, e.g. via /admin/say_as).
                      const isNpcMessage =
                        !!m.user
                        && !m.system
                        && !m.narration
                        && (
                          ALL_NPC_DIRECTORY.some((n) => n.name === m.user)
                          || (liveNpcs[room] || []).some((n) => n.name === m.user)
                        );
                      const saveAsPastRp = () => {
                        const idx = messages.findIndex((x) => x.id === m.id);
                        const context = messages.slice(Math.max(0, idx - 5), idx + 5)
                          .map((x) => ({ user: x.user, color: x.color, text: x.text }));
                        axios.post(`${API}/npc/past_rps/save`, {
                          user_id: userId,
                          npc: m.user,
                          title: `${m.user.replace('_', ' ')}: ${(m.text || '').slice(0, 40)}`,
                          excerpt: m.text || '',
                          messages: context,
                        })
                          .then(() => pushLocalSystem(`Saved RP moment with ${m.user.replace('_', ' ')}.`))
                          .catch(() => pushLocalSystem('Could not save RP.'));
                      };
                      return (
                      <div key={m.id} className="mb-[3px]">
                        {showTimestamps && (
                          <span className="text-[11px] text-gray-500">[{m.time}]</span>
                        )}{' '}
                        {isNpcMessage && (
                          <span
                            data-testid={`save-rp-${m.id}`}
                            onClick={saveAsPastRp}
                            title={`Remember this moment with ${m.user.replace('_', ' ')}`}
                            style={{
                              display: 'inline-block', cursor: 'pointer',
                              color: '#5a4a1d', fontSize: 12, marginRight: 4,
                              userSelect: 'none',
                            }}
                          >▢</span>
                        )}
                        {m.narration ? (
                          <span style={{ color: '#4a3a18', fontStyle: 'italic', display: 'block',
                            padding: '6px 10px', borderLeft: '3px solid #5a4a1d',
                            background: 'rgba(248,243,224,0.6)', margin: '4px 0', whiteSpace: 'pre-wrap' }}>
                            {m.text}
                          </span>
                        ) : m.system ? (
                          <span style={{ color: m.color, fontStyle: 'italic' }}>*** {m.text} ***</span>
                        ) : m.whisper ? (
                          // Msg #989 — /whisper + /to render as bracketed
                          // "[reccult whispers to Morrigan]" chatroom lines
                          // (never enters Messenger, never carries a colon).
                          <span
                            data-testid={`chat-whisper-line-${m.id}`}
                            style={{ color: m.color || '#5a3a8a', fontStyle: 'italic' }}
                          >
                            <b style={{ marginRight: 4 }}>{m.user}</b>
                            <span dangerouslySetInnerHTML={{ __html: renderInline(m.text) }} />
                          </span>
                        ) : m.action ? (
                          <span style={{ color: m.color || '#111111', fontStyle: 'italic' }}>
                            *{' '}
                            {/* Msg #973 — username click: NPCs open profile, users prompt a /to whisper. */}
                            <b
                              data-testid={`chat-user-name-${m.user}`}
                              style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                              onClick={() => {
                                const npc = (liveNpcs[room] || []).find((n) => n.name === m.user);
                                if (npc) { openProfile({ ...npc, isNPC: true }); return; }
                                if (m.user && m.user !== username) {
                                  const base = m.user.replace(/_\(fainted\)$/, '');
                                  setInput(`/to ${base} `);
                                  setTimeout(() => {
                                    const el = document.querySelector('[data-testid="chat-input"]');
                                    if (el) el.focus();
                                  }, 20);
                                } else {
                                  setViewChar({ name: m.user, color: m.color, status: `In ${room}` });
                                }
                              }}>{m.user}</b>{' '}
                            <span style={{ color: m.color || '#111111' }} dangerouslySetInnerHTML={{ __html: renderInline(m.text) }} />
                          </span>
                        ) : (
                          <>
                            {/* Msg #973 — username click: NPCs open profile, users prompt a /to whisper. */}
                            <span
                              data-testid={`chat-user-name-${m.user}`}
                              style={{ color: m.color, fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline dotted' }}
                              onClick={() => {
                                const npc = (liveNpcs[room] || []).find((n) => n.name === m.user);
                                if (npc) { openProfile({ ...npc, isNPC: true }); return; }
                                if (m.user && m.user !== username) {
                                  const base = m.user.replace(/_\(fainted\)$/, '');
                                  setInput(`/to ${base} `);
                                  setTimeout(() => {
                                    const el = document.querySelector('[data-testid="chat-input"]');
                                    if (el) el.focus();
                                  }, 20);
                                } else {
                                  setViewChar({ name: m.user, color: m.color, status: `In ${room}` });
                                }
                              }}>{m.user}:</span>{' '}
                            {/* Msg #973 — message text also uses the sender's color, not just the username. */}
                            <span style={{ color: m.color || '#111111' }} dangerouslySetInnerHTML={{ __html: renderInline(m.text) }} />
                          </>
                        )}
                      </div>
                    );})}
                    {npcTyping && (
                      <div className="text-[11px] mt-2" style={{ color: '#8B2C2C', fontStyle: 'italic' }}>
                        {npcTyping.replace('_', ' ')} is composing a reply...
                      </div>
                    )}
                  </div>
                </td>
                <td className="align-top pl-2" style={{ width: '28%' }}>
                  <RoomDirectory
                    rooms={(() => {
                      // Msg #975 — private rooms the user has JOINED should stay
                      // visible in the room-directory even after switching to a
                      // public room (recurring bug from earlier iterations).
                      const priv = (knownPrivateRooms || [])
                        .map((p) => p && p.room)
                        .filter(Boolean);
                      // Msg #1003 — Also surface PUBLIC_ROOMS (user-created,
                      // open-to-all rooms) in the userlist so folks can hop
                      // to them + see who's inside. Private rooms remain
                      // restricted to the invited caller.
                      // Msg #1003 (continued) — Derive user-created rooms
                      // from live presence data. Any room in presenceUsers
                      // that isn't in DEFAULT_ROOMS/PUBLIC_ROOMS is a custom
                      // room. Private rooms (`private_*`) still only show up
                      // if the current user is invited (knownPrivateRooms).
                      const dynamicRooms = Array.from(new Set(
                        (presenceUsers || [])
                          .map((u) => u.room)
                          .filter((r) => r && !DEFAULT_ROOMS.includes(r) && !PUBLIC_ROOMS.includes(r))
                          .filter((r) => {
                            if (!String(r).startsWith('private_')) return true;
                            // For private rooms: admin sees ALL; users only see ones they're in / invited to.
                            if (isAdmin) return true;
                            if (r === room) return true;
                            return priv.includes(r);
                          })
                      ));
                      const uniq = [...new Set([
                        ...(room && String(room).startsWith('private_') ? [room] : []),
                        ...priv,
                        ...DEFAULT_ROOMS,
                        ...PUBLIC_ROOMS,
                        ...dynamicRooms,
                      ])];
                      return uniq;
                    })()}
                    currentRoom={room}
                    presence={presence}
                    npcs={liveNpcs}
                    arousedNpcs={arousedNpcs}
                    speakingSet={speakingSet}
                    onSwitchRoom={(r, opts) => {
                      // Msg #1004 — Admin "peek" auto-enables cloak so
                      // stepping into a private room doesn't tip the users.
                      if (opts && opts.peek && isAdmin) {
                        if (!cloaked) toggleCloak(true);
                        pushLocalSystem(`🥷 Peeking into ${r} cloaked. Uncloak from the ADMIN tab when done.`);
                      }
                      setRoom(r); sessionStorage.setItem('keep_room', r);
                    }}
                    onViewUser={(u) => openProfile(u)}
                    onRefresh={() => setRefreshTick((t) => t + 1)}
                    onBlockUser={blockUser}
                    onUnblockUser={unblockUser}
                    onMessageUser={openPmWith}
                    blockedUsers={blockedUsers}
                    isAdmin={isAdmin}
                    voiceRoomMode={voiceRoomMode}
                    voiceOpen={voiceOpen}
                    onToggleVoice={() => setVoiceOpen((v) => !v)}
                    actionsBar={
                      <TopTabsBar
                        onOpenPm={() => { setPmOpen(true); setPmMinimized(false); refreshFriends(); }}
                        unreadPm={unreadPmCount}
                        onOpenWorldMap={() => setShowWorldMap(true)}
                        onOpenLeaderboard={() => setShowLeaderboard(true)}
                        onOpenQuestBoard={() => setShowQuestBoard(true)}
                        onOpenAddFriend={() => setShowAddFriend(true)}
                        onOpenFriendInbox={() => setShowFriendInbox(true)}
                        pendingInboxCount={pendingInboxCount}
                        onOpenRegister={() => setShowRegister(true)}
                        onOpenEditProfile={() => setShowEdit(true)}
                        onOpenDelete={() => setShowDelete(true)}
                        isAdmin={isAdmin}
                        isMod={isMod}
                        cloaked={cloaked}
                        onToggleCloak={toggleCloak}
                        onOpenPrivateRoomMonitor={isAdmin ? () => setShowPrivateRoomMonitor(true) : undefined}
                        // Msg #995 — Friends List pill toggles the standalone box
                        friendsCount={(friends || []).length}
                        friendsBoxOpen={friendsBoxOpen}
                        onToggleFriendsBox={() => {
                          setFriendsBoxOpen((v) => !v);
                          setFriendsBoxMin(false);
                          refreshFriends();
                        }}
                        // Msg #995 — Admin Control / Recap toggles
                        adminBoxOpen={adminBoxOpen}
                        onToggleAdminBox={() => setAdminBoxOpen((v) => !v)}
                        whisperShadowOpen={
                          whisperShadowOverride == null
                            ? (isAdmin && cloaked)
                            : whisperShadowOverride
                        }
                        onToggleWhisperShadow={isAdmin ? () => {
                          const currentlyOpen = whisperShadowOverride == null
                            ? (isAdmin && cloaked)
                            : whisperShadowOverride;
                          setWhisperShadowOverride(!currentlyOpen);
                        } : undefined}
                      />
                    }
                    footerExtra={null}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {/* Msg #974 — Typing indicator strip. Renders only when peers active. */}
          {typingPeers.length > 0 && (
            <div
              data-testid="typing-indicator"
              style={{
                marginTop: 8,
                padding: '4px 10px',
                fontSize: 12,
                fontStyle: 'italic',
                background: 'var(--room-bg, #fafaf7)',
                border: '1px solid var(--room-border, #c8c8b8)',
                color: 'var(--room-text, #444)',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <span style={{ letterSpacing: 2, opacity: 0.7 }}>…</span>
              {typingPeers.slice(0, 3).map((p, i) => (
                <React.Fragment key={p.name}>
                  {i > 0 && <span>, </span>}
                  <span style={{ color: p.color || 'inherit', fontWeight: 'bold' }}>{p.name}</span>
                </React.Fragment>
              ))}
              {typingPeers.length > 3 && <span> and {typingPeers.length - 3} others</span>}
              <span style={{ marginLeft: 4 }}>
                {typingPeers.length === 1 ? 'is typing' : 'are typing'}
                <span className="typing-dots" style={{ marginLeft: 2 }}>…</span>
              </span>
            </div>
          )}

          <Composer
            input={input}
            setInput={setInput}
            onSend={send}
            onTyping={notifyTyping}
            myColor={myColor}
            setMyColor={setMyColor}
            composeMode={composeMode}
            setComposeMode={setComposeMode}
            systemColor={SYSTEM_COLOR}
          />

          {/* Msg #980 — Combat / Spell action-pill bar (extracted to ActionPillBar.jsx). */}
          <ActionPillBar
            npcsHere={(liveNpcs[room] || []).filter((n) => !!n.name)}
            onCommand={send}
          />

          {/* Msg #981 + #982 — Voice / Video / Screenshare toggle pill (LiveKit).
              Rooms with a voice/video/screenshare preset show a live status pill;
              Discord-linked rooms show a 🔗 Discord badge next to it. */}
          <div
            data-testid="voice-video-toggle-row"
            className="mt-2 flex flex-wrap items-center gap-2"
            style={{
              padding: '5px 10px',
              border: '1px solid var(--room-border, #c8c8b8)',
              background: 'var(--room-bg, #fafaf7)',
              fontSize: 12,
            }}
          >
            <span style={{ fontWeight: 'bold', letterSpacing: 1, color: 'var(--room-text, #111)' }}>
              {voiceRoomMode === 'screenshare' ? 'SCREEN:' : (voiceRoomMode === 'voice_video' ? 'STREAM:' : 'VOICE:')}
            </span>
            <button
              data-testid="voice-video-toggle"
              onClick={() => setVoiceOpen((v) => !v)}
              title={voiceReady
                ? (voiceOpen ? 'Leave voice/video' : `Join voice/video in ${room}`)
                : 'Voice/Video not configured — Admin needs to drop LiveKit keys.'}
              style={{
                background: voiceOpen ? '#8b1a1a' : (voiceReady ? '#2d6b1e' : '#8a8a7a'),
                color: '#f5f5f0',
                border: '1px solid rgba(0,0,0,0.35)',
                padding: '3px 12px',
                fontSize: 12, fontWeight: 'bold',
                letterSpacing: 1, borderRadius: 16,
                cursor: 'pointer',
                opacity: voiceReady ? 1 : 0.7,
              }}
            >{voiceOpen
              ? '🚪 Leave'
              : (voiceRoomMode === 'screenshare' ? '🖥 Join Screenshare' : '🎙 Join Voice')}</button>
            {DISCORD_LINKED_ROOMS.has(room) && (
              <span
                data-testid="discord-linked-badge"
                title="Chat in this room mirrors to the linked Discord channel."
                style={{
                  background: '#5865F2', color: '#fff',
                  padding: '2px 8px', borderRadius: 12,
                  fontSize: 10, fontWeight: 'bold', letterSpacing: 1,
                }}
              >🔗 Discord</span>
            )}
            <span style={{ fontSize: 11, color: '#5a5a4a' }}>
              {voiceOpen
                ? (voiceRoomMode === 'screenshare'
                    ? 'Screenshare active — click the monitor button in the panel below to share.'
                    : 'Live — bottom-right panel.')
                : (voiceReady
                    ? (voiceRoomMode === 'screenshare'
                        ? 'Movie Night — join to share a screen.'
                        : (voiceRoomMode === 'voice_video'
                            ? 'Hangout — free voice + video + screenshare with everyone here.'
                            : 'Free voice + video + screenshare with everyone in this room.'))
                    : 'Not yet configured — Admin, drop LiveKit keys into backend/.env.')}
            </span>
          </div>

          <div className="keep-footer-text mt-3">© Mandala &nbsp;|&nbsp; Based on PHPMyChat</div>
        </div>
      </div>

      <div className="keep-disclaimer">© Mandala - All rights reserved</div>

      <ViewProfileModal
        open={!!viewChar}
        character={viewChar ? applyOverride(viewChar) : null}
        isAdmin={isAdmin}
        userId={userId}
        apiBase={API}
        onEditNpc={(c) => { setEditNpcChar(c); setViewChar(null); }}
        onDeleteNpc={async (npcName) => {
          try {
            const r = await axios.delete(`${API}/admin/npc/${encodeURIComponent(npcName)}`, {
              params: { requester: username },
            });
            const removed = r.data?.removed ?? 0;
            pushLocalSystem(removed
              ? `Deleted NPC "${npcName}".`
              : `NPC "${npcName}" is a built-in and cannot be deleted (nothing removed).`);
          } catch (e) {
            const d = e && e.response && e.response.data && e.response.data.detail;
            pushLocalSystem(d || `Failed to delete NPC ${npcName}.`);
          }
        }}
        onContinueScene={continueScene}
        onContinueRp={async (char, rp) => {
          // Msg #797 batch — Post the last saved line from this Past RP into
          // the current room, spoken by the NPC herself. Uses /npc/silent_join
          // to make her present, then /npc/say to broadcast the line.
          try {
            const lastLine = (rp.messages || [])
              .slice()
              .reverse()
              .find((mm) => String(mm.user || '').toLowerCase() === String(char.name || '').toLowerCase());
            const text = lastLine ? lastLine.text : (rp.messages || []).slice(-1)[0]?.text;
            if (!text) return;
            // /npc/silent_join requires requester_user_id + requester_name so
            // the backend can attribute the roleplay hook to a real caller.
            await axios.post(`${API}/npc/silent_join`, {
              npc: char.name,
              room,
              requester_user_id: userId,
              requester_name: displayName(),
            });
            await axios.post(`${API}/npc/say`, {
              npc: char.name, room, text, requester: username,
            });
            pushLocalSystem(`▶ Continuing RP with ${char.name.replace('_', ' ')} in ${room}.`);
            setViewChar(null);
          } catch (e) {
            pushLocalSystem(`Could not continue RP: ${e.message || e}`);
          }
        }}
        onPm={openPmWith}
        onSendFriendRequest={(c) => { setFriendReqTarget(c); }}
        myName={displayName()}
        cloaked={cloaked}
        onToggleCloak={toggleCloak}
        puppetNpc={puppetNpc}
        onTogglePuppet={(next) => {
          setPuppetNpc(next);
          if (next) {
            pushLocalSystem(`🎭 Puppet mode ON — next messages will speak as ${next.name.replace('_', ' ')}.`);
          } else {
            pushLocalSystem(`🎭 Puppet released.`);
          }
        }}
        onOpenEditProfile={(targetChar) => {
          // Msg #992 — Admin editing another user's profile uses the full
          // AdminEditProfileModal (mirrors the regular Edit Profile UX).
          if (targetChar && targetChar.name && targetChar.name !== username) {
            setAdminEditTarget(targetChar);
          } else {
            setShowEdit(true);
          }
        }}
        onDeleteUser={async (targetName, hard) => {
          try {
            const r = await axios.post(`${API}/admin/delete_user`, {
              requester: username, username: targetName, hard: !!hard,
            });
            const summary = r.data?.deleted
              ? Object.entries(r.data.deleted).filter(([, v]) => v > 0)
                  .map(([k, v]) => `${k}:${v}`).join(', ') || 'no rows'
              : 'ok';
            pushLocalSystem(`Deleted ${targetName} (${hard ? 'HARD' : 'soft'}). ${summary}.`);
          } catch (e) {
            const d = e && e.response && e.response.data && e.response.data.detail;
            pushLocalSystem(d || `Failed to delete ${targetName}.`);
          }
        }}
        requesterName={username}
        onClose={() => setViewChar(null)}
      />
      <EditNpcModal
        open={!!editNpcChar}
        character={editNpcChar ? applyOverride(editNpcChar) : null}
        requester={username}
        apiBase={API}
        onClose={(saved) => {
          if (saved && editNpcChar) {
            setNpcOverrides((prev) => ({
              ...prev,
              [editNpcChar.name]: {
                ...(prev[editNpcChar.name] || {}),
                description: saved.description,
                picture: saved.picture,
                // Msg #797 — carry through the gallery + feminine variant so the
                // ViewProfileModal (and reopened EditNpcModal) reflect the save
                // immediately, without waiting for the 60 s /npc_overrides refetch.
                pictures: saved.pictures || [],
                feminine_picture: saved.feminine_picture || null,
                // Msg #797 batch — website + embedded video
                website: saved.website || null,
                video_url: saved.video_url || null,
                // Msg #798 batch — dedicated GIF + music-player HTML
                gif_url: saved.gif_url || null,
                music_html: saved.music_html || null,
                color: saved.color,
              },
            }));
          }
          setEditNpcChar(null);
        }}
      />
      <RegisterModal open={showRegister} onClose={() => setShowRegister(false)} />
      <EditProfileModal open={showEdit} onClose={() => setShowEdit(false)} userId={userId} apiBase={API} />
      <DeleteUserModal open={showDelete} onClose={() => setShowDelete(false)} />
      <TutorialModal open={showTutorial} onClose={() => setShowTutorial(false)} onInsertCommand={(c) => setInput(c)} />
      <SiteRulesModal open={showRules} onClose={() => setShowRules(false)} />
      <WorldMapModal
        open={showWorldMap}
        currentRoom={room}
        apiBase={API}
        userId={userId}
        onJoin={(r) => {
          setShowWorldMap(false);
          setRoom(r);
          sessionStorage.setItem('keep_room', r);
        }}
        onClose={() => setShowWorldMap(false)}
        onOpenProfile={(char) => {
          setShowWorldMap(false);
          openProfile(char);
        }}
      />
      <LeaderboardModal
        open={showLeaderboard}
        apiBase={API}
        myUsername={username}
        onClose={() => setShowLeaderboard(false)}
      />
      <FriendRequestSendModal
        open={!!friendReqTarget}
        target={friendReqTarget}
        apiBase={API}
        userId={userId}
        myUsername={username}
        onFriendAdded={(n) => setFriends((prev) => Array.from(new Set([...(prev || []), n])))}
        onClose={() => setFriendReqTarget(null)}
      />
      <FriendRequestInboxModal
        open={showFriendInbox}
        apiBase={API}
        myUsername={username}
        onAccepted={(n) => setFriends((prev) => Array.from(new Set([...(prev || []), n])))}
        onClose={() => setShowFriendInbox(false)}
      />
      <AddFriendSearchModal
        open={showAddFriend}
        apiBase={API}
        userId={userId}
        myUsername={username}
        onFriendAdded={(n) => setFriends((prev) => Array.from(new Set([...(prev || []), n])))}
        onClose={() => setShowAddFriend(false)}
      />
      <WhisperShadowConsole
        open={
          whisperShadowOverride == null
            ? (isAdmin && cloaked)
            : (isAdmin && whisperShadowOverride)
        }
        apiBase={API}
        requester={username}
        onOpenRecap={() => setShowSceneRecap(true)}
      />
      {/* Msg #981 — Voice / Video / Screenshare panel (LiveKit). */}
      {voiceOpen && (
        <VoiceVideoPanel
          room={room}
          userId={userId}
          userName={username}
          mode={voiceRoomMode === 'screenshare' ? 'screenshare' : 'voice_video'}
          onLeave={() => setVoiceOpen(false)}
        />
      )}
      <KickDurationModal
        open={!!kickModal}
        target={kickModal ? kickModal.target : ''}
        action={kickModal ? kickModal.action : 'kick'}
        apiBase={API}
        requester={username}
        onDone={(opt) => {
          const verb = kickModal && kickModal.action === 'ban' ? 'Banned' : 'Kicked';
          pushLocalSystem(`${verb} ${kickModal.target}${opt.minutes ? ` (${opt.label})` : ''}.`);
        }}
        onClose={() => setKickModal(null)}
      />
      <PrivateRoomMonitorModal
        open={showPrivateRoomMonitor && isAdmin}
        apiBase={API}
        requester={username}
        cloaked={cloaked}
        onJoin={(r) => {
          setRoom(r);
          sessionStorage.setItem('keep_room', r);
          pushLocalSystem(`Shadow-joined ${r}.`);
        }}
        onClose={() => setShowPrivateRoomMonitor(false)}
      />
      <UnreadPmModal
        open={unreadModalOpen}
        total={unreadInfo.total}
        senders={unreadInfo.by_sender}
        onOpenMessenger={() => {
          setUnreadModalOpen(false);
          setPmOpen(true);
          setPmMinimized(false);
        }}
        onDismiss={() => setUnreadModalOpen(false)}
      />
      <MessengerBox
        open={pmOpen}
        minimized={pmMinimized}
        whispers={whispers.filter((w) => !blockedUsers.has(String(w.from_name || '').toLowerCase()))}
        myUserId={userId}
        myName={displayName()}
        unreadCount={unreadPmCount}
        onSendWhisper={(to, txt) => sendWhisper(to, txt, 'pm')}
        onMarkRead={markConversationRead}
        onMinimize={() => setPmMinimized((v) => !v)}
        onClose={() => { setPmOpen(false); setPmMinimized(false); }}
        closedConvs={closedConvs}
        onCloseConversation={closeConversation}
        onReopenConversation={reopenConversation}
        typingBySender={typingBySender}
        onTypingHeartbeat={sendTypingHeartbeat}
        onBlockUser={blockUser}
        requestedActiveKey={requestedPmKey}
        onActiveKeyConsumed={() => setRequestedPmKey(null)}
        friends={friends}
        blockedUsers={blockedUsers}
        onlineUsers={onlineUsers}
        onAddFriend={addFriend}
        onRemoveFriend={removeFriend}
        onOpenProfile={openProfile}
      />

      {/* Msg #800 enhancement — Floating Adventure Soundtrack player.
          Auto-plays the music of whichever saved NPC is in the current room. */}
      <AdventureSoundtrackPlayer
        room={room}
        presenceNames={(presenceUsers || []).map((u) => u.name)}
        npcLocations={npcLocations}
        apiBase={API}
      />

      {/* Msg #993 — Standalone Friends List Box (decoupled from Messenger). */}
      <FriendsListBox
        open={friendsBoxOpen}
        minimized={friendsBoxMin}
        friends={friends}
        onlineUsers={onlineUsers}
        blockedUsers={blockedUsers}
        onAddFriend={addFriend}
        onRemoveFriend={removeFriend}
        onBlockUser={blockUser}
        onOpenConversation={({ name }) => openPmWith({ name })}
        onMinimize={() => setFriendsBoxMin((v) => !v)}
        onClose={() => setFriendsBoxOpen(false)}
      />

      {/* Msg #996 — Right-sidebar AdminModPanel (only when admin_control_pill is
          toggled ON via the top pill row). No wrapping "SOCIAL / ADMIN" tiles —
          those visual slots duplicated the pill row and were removed per Msg #996. */}
      {(isAdmin || isMod) && adminBoxOpen && (
        <div
          data-testid="admin-panel-anchor"
          style={{
            position: 'fixed', right: 12, top: 100, width: 280, zIndex: 40,
          }}
        >
          <AdminModPanel
            role={isAdmin ? 'admin' : 'mod'}
            cloaked={cloaked}
            onToggleCloak={() => toggleCloak(!cloaked)}
            privateRoomsAllowed={privateRoomsAllowed}
            onTogglePrivateRooms={() => setPrivateRoomsAllowed((v) => !v)}
            puppetNpc={puppetNpc && puppetNpc.name}
            onOpenBan={() => setShowBanModal(true)}
            onOpenPuppet={() => setShowPuppetModal(true)}
            onOpenAnnounce={() => setShowAnnounceModal(true)}
            onOpenDeleteUser={isAdmin ? () => setShowDeleteUserModal(true) : undefined}
            onOpenShadowWhisper={isAdmin ? () => setShowShadowWhisperModal(true) : undefined}
            onOpenPrivateRoomMonitor={isAdmin ? () => setShowPrivateRoomMonitor(true) : undefined}
            onOpenManageMods={isAdmin ? () => setShowManageModsModal(true) : undefined}
            onOpenAddStatPoints={isAdmin ? () => setShowAddStatPointsModal(true) : undefined}
            onOpenEditUserStats={isAdmin ? () => setShowEditUserStatsModal(true) : undefined}
            onOpenEditNpcAffection={isAdmin ? () => setShowEditNpcAffectionModal(true) : undefined}
            onGrandfatherToday={isAdmin ? (async () => {
              try {
                const r = await axios.post(`${API}/admin/grandfather_today`, { requester: username });
                const g = (r.data?.granted || []).length;
                const s = (r.data?.skipped || []).length;
                pushLocalSystem(`🎁 Grandfathered today: ${g} granted, ${s} skipped.`);
              } catch (e) {
                const d = e && e.response && e.response.data && e.response.data.detail;
                pushLocalSystem(d || 'Grandfather-today failed.');
              }
            }) : undefined}
            onOpenCreateNpc={isAdmin ? () => {
              setEditingNpc(null);
              setShowCreateNpcModal(true);
            } : undefined}
            onOpenManageNpcs={isAdmin ? () => setShowManageNpcsModal(true) : undefined}
            onOpenUsersList={isAdmin ? () => setShowUsersListModal(true) : undefined}
            themeColor={roomThemeBg}
          />
        </div>
      )}

      <BanUserModal
        open={showBanModal}
        onClose={() => setShowBanModal(false)}
        onSubmit={async (name, minutes) => {
          try {
            await axios.post(`${API}/admin/ban`, {
              requester: username, target_name: name, minutes,
            });
            pushLocalSystem(`${name} has been banned for ${minutes} minutes.`);
          } catch (e) {
            const d = e && e.response && e.response.data && e.response.data.detail;
            pushLocalSystem(d || `Failed to ban ${name}.`);
          }
        }}
      />
      <PuppetNpcModal
        open={showPuppetModal}
        onClose={() => setShowPuppetModal(false)}
        currentPuppet={puppetNpc && puppetNpc.name}
        onSubmit={(name) => {
          if (!name) { setPuppetNpc(null); pushLocalSystem('Puppet released.'); return; }
          setPuppetNpc({ name });
          pushLocalSystem(`Puppeting ${name.replace('_', ' ')}. Type in chat to speak as them.`);
        }}
      />
      <AnnounceModal
        open={showAnnounceModal}
        onClose={() => setShowAnnounceModal(false)}
        onSubmit={async (text) => {
          try {
            await axios.post(`${API}/admin/announce`, {
              requester: username, message: text, room: room,
            });
            pushLocalSystem(`Announcement delivered by Goddess_Sophetia.`);
          } catch (e) {
            const d = e && e.response && e.response.data && e.response.data.detail;
            pushLocalSystem(d || 'Failed to send announcement.');
          }
        }}
      />

      {/* Msg #986 — Admin: delete a user account. */}
      <DeleteUserAdminModal
        open={showDeleteUserModal && isAdmin}
        onClose={() => setShowDeleteUserModal(false)}
        onSubmit={async (target, hard) => {
          try {
            const r = await axios.post(`${API}/admin/delete_user`, {
              requester: username, username: target, hard: !!hard,
            });
            const summary = r.data?.deleted
              ? Object.entries(r.data.deleted).filter(([, v]) => v > 0)
                  .map(([k, v]) => `${k}:${v}`).join(', ') || 'no rows'
              : 'ok';
            pushLocalSystem(`Deleted ${target} (${hard ? 'HARD' : 'soft'}). ${summary}.`);
          } catch (e) {
            const d = e && e.response && e.response.data && e.response.data.detail;
            pushLocalSystem(d || `Failed to delete ${target}.`);
          }
        }}
      />

      {/* Msg #987 — Admin: red-themed shadow whisper MONITOR (read-only). */}
      <ShadowWhisperMonitorModal
        open={showShadowWhisperModal && isAdmin}
        onClose={() => setShowShadowWhisperModal(false)}
        requester={username}
      />

      {/* Msg #987 — Admin: manage moderators (list + promote + demote). */}
      <ManageModsModal
        open={showManageModsModal && isAdmin}
        onClose={() => setShowManageModsModal(false)}
        requester={username}
      />

      {/* Msg #988 — Admin: grant stat points / stat overrides. */}
      <AddStatPointsModal
        open={showAddStatPointsModal && isAdmin}
        onClose={() => setShowAddStatPointsModal(false)}
        onSubmit={async (payload) => {
          const r = await axios.post(`${API}/admin/user_stats`, {
            requester: username, ...payload,
          });
          const rk = r.data?.rank?.rank || '?';
          pushLocalSystem(`⚡ ${payload.target}: kills=${r.data?.kills} rank=${rk}.`);
        }}
      />

      {/* Msg #994 — Admin: view + edit a user's live Character Stats. */}
      <AdminEditUserStatsModal
        open={showEditUserStatsModal && isAdmin}
        onClose={() => setShowEditUserStatsModal(false)}
        requester={username}
      />

      {/* Msg #994 — Admin: view + edit NPC affection / dislike toward a user. */}
      <AdminEditNpcAffectionModal
        open={showEditNpcAffectionModal && isAdmin}
        onClose={() => setShowEditNpcAffectionModal(false)}
        requester={username}
      />

      {/* Msg #996 — Pokémon Stadium turn-based battle overlay. Self-manages
          polling; only mounts when the user actually has a battle. */}
      <PokemonStadiumBattle
        apiBase={API}
        username={username}
        room={room}
      />

      {/* Msg #990 — Admin: create + manage custom NPCs. */}
      <CreateNpcModal
        open={showCreateNpcModal && isAdmin}
        onClose={() => { setShowCreateNpcModal(false); setEditingNpc(null); }}
        requester={username}
        initial={editingNpc}
        onSaved={(npc) => {
          if (npc && npc.name) pushLocalSystem(`🐣 Saved custom NPC: ${npc.name}.`);
        }}
      />
      <ManageNpcsModal
        open={showManageNpcsModal && isAdmin}
        onClose={() => setShowManageNpcsModal(false)}
        requester={username}
        onEdit={(n) => {
          setEditingNpc(n);
          setShowManageNpcsModal(false);
          setShowCreateNpcModal(true);
        }}
      />

      {/* Msg #991 — Admin: browse ALL users, click name to open profile. */}
      <UsersListModal
        open={showUsersListModal && isAdmin}
        onClose={() => setShowUsersListModal(false)}
        requester={username}
        onView={(char) => {
          setViewChar(char);
          setShowUsersListModal(false);
        }}
      />

      {/* Msg #992 — Admin: proper Edit Profile modal for another user. */}
      <AdminEditProfileModal
        open={!!(adminEditTarget && isAdmin)}
        target={adminEditTarget}
        requester={username}
        onClose={() => setAdminEditTarget(null)}
      />

      {/* Session AD (Msg #972) — Floating HP pill */}
      <HpPill apiBase={API} userId={userId} room={room} />

      {/* Msg #975 — Daily Quest Board */}
      <DailyQuestModal
        open={showQuestBoard}
        onClose={() => setShowQuestBoard(false)}
        apiBase={API}
        userId={userId}
        onClaimed={(d) => pushLocalSystem(
          `Quest claimed. Reward: ${d.reward.kills > 0 ? `+${d.reward.kills} kill ` : ''}${d.reward.hp > 0 ? `+${d.reward.hp} HP ` : ''}${d.reward.mp > 0 ? `+${d.reward.mp} MP` : ''}`.trim()
        )}
      />

      {/* Msg #977 — Scene Recap modal */}
      <SceneRecapModal
        open={showSceneRecap}
        onClose={() => setShowSceneRecap(false)}
        apiBase={API}
        room={room}
        username={username}
      />

      {/* Msg #804 — Friend-request toast */}
      {friendReqToast && (
        <div
          data-testid="friend-request-toast"
          onClick={() => { setFriendReqToast(null); setShowFriendInbox(true); }}
          style={{
            position: 'fixed', top: 20, right: 20, zIndex: 200,
            background: '#e9dab0', color: '#3a2b14',
            border: '3px double #5a4a1d', padding: '12px 18px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.55)',
            fontFamily: 'Georgia, serif', fontSize: 13, cursor: 'pointer',
            animation: 'mandala-slide 300ms ease-out',
            maxWidth: 320,
          }}
        >
          <div style={{ fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
            ✉ You&apos;ve got mail
          </div>
          <div><b>{friendReqToast.from}</b> sent you a friend request.</div>
          <div style={{ marginTop: 4, fontSize: 11, fontStyle: 'italic', opacity: 0.75 }}>
            Click to open the request inbox.
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
